import {
  CheckPicker,
  Loader,
  Message,
  Popover,
  SelectPicker,
  Toggle,
  Tooltip,
  useToaster,
  Whisper,
} from "rsuite";
import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import {
  getBoardCostReportDataForExcel,
  getRecommendedBCDataForExcel,
  getSKUAnalysisData,
  useBusinessCostData,
  useBusinessCostFilter,
  useOtherSKUsBC,
  useSKUAnalysisData,
  useUpdateBusinessCost,
  useResetBusinessCost,
} from "../../../react-query/hooks/HBusinessCostSetting";
import DangerArrowUpSm from "../../../assets/up-arrow-sm-danger.svg";
import WarnArrowUpSm from "../../../assets/up-arrow-sm-warn.svg";
import SafeArrowDownSm from "../../../assets/up-arrow-sm-safe.svg";
import moment from "moment";
import { Column } from "primereact/column";
import InfoOutline from "../../../assets/info-outline.svg";
import ProductInfoIcon from "../../../assets/info-circle-grey.svg";
import {
  sortCommodityFirst,
  getDefaultCommodity,
} from "../../../utils/helpers/commodityOrder";
import {
  dateFormatterToDDMMMYYYY,
  formatNumberwithCommas,
  numberFormatter,
} from "../../../utils/formatter";
import {
  BusinessCostData,
  BusinessCostSkuUpdatePayload,
  BusinessCostUpdatePayload,
  BusinessCostResetPayload,
  WireBenchmarkItem,
} from "../../../services/interfaces/IBusinessCostSetting";
import {
  APPROVAL_STATUS,
  Error_MESSAGE,
  USER_ROLE,
} from "../../../utils/constants";
import { useAppSelector } from "../../../services/redux/hooks/hooks";
import Button from "../../../components/button";
import EditIcon from "../../../assets/pencil.png";
import ApproveIcon from "../../../assets/check-decagram.svg";
import CloseIcon from "../../../assets/cross.svg";
import { InputNumber, InputNumberChangeEvent } from "primereact/inputnumber";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import useResponsiveWidth from "../../../react-query/hooks/useResponsiveWidth";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { appInsights } from "../../../router/router";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../react-query/queryKeys";
import {
  convertDateToMonthYear,
  convertDateToFullDate,
} from "../../../utils/helpers/mapModulesToIcon";
import JSZip from "jszip";
import CustomModal from "../../../components/custom-modal";
import { useLocation } from "react-router-dom";
import { trackTabClick } from "../../../utils/helpers/trackingEvent";
import SortDefault from "../../../assets/sort_default.svg";
import SortAsc from "../../../assets/sort_asc.svg";
import SortDsc from "../../../assets/sort_desc.svg";
import WirePopupModal from "../../../components/wire-popup";

type BusinessCostTabKey = "benchmarkBoardSetting" | "skuBoardAnalysis";

type BusinessCostTab = {
  key: BusinessCostTabKey;
  label: string;
  trackingLabel: string;
};

const BusinessCostSetting = () => {
  const queryClient = useQueryClient();
  const toaster = useToaster();
  const width = useResponsiveWidth();
  const userDetailsRedux = useAppSelector((state) => state.user);
  const isDimUser = Boolean(userDetailsRedux?.role_name === "DIM");
  const statusClasses = {
    [APPROVAL_STATUS.NoChange]: "border-[#A899F1] bg-[#F7F5FF] text-[#3818D9]",
    [APPROVAL_STATUS.Self]: "border-deloitteGreen bg-[#F0F9F2] text-[#03781D]",
    [APPROVAL_STATUS.Approved]:
      "border-deloitteGreen bg-[#F0F9F2] text-[#03781D]",
    [APPROVAL_STATUS.Pending]: "border-[#E2A1A1] bg-[#F9F2F2] text-[#BA1717]",
    [APPROVAL_STATUS.Requested]: "border-[#79B0E2] bg-[#F0F8FF] text-[#006FD5]",
  };
  const location = useLocation();
  const hiddenParam = location.state?.hiddenParam;

  const [trigger, setTrigger] = useState<number>(0);
  const [commodity, setCommodity] = useState<null | string>(null);
  const [district, setDistrict] = useState<null | string>(null);
  const [baseSKU, setBaseSKU] = useState<null | string>(null);
  const [navItem, setNavItem] = useState<string>(
    hiddenParam === undefined || hiddenParam === "" ? "" : hiddenParam,
  );
  const [isSKUBoardDownload, setIsSKUBoardDownload] = useState(false);
  const [showSKUActions, setShowSKUActions] = useState(false);
  const [showResetMenu, setShowResetMenu] = useState(false);
  const resetMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!showResetMenu) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resetMenuRef.current &&
        !resetMenuRef.current.contains(event.target as Node)
      ) {
        setShowResetMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showResetMenu]);

  const { fetchFilters: filters, isLoading } = useBusinessCostFilter(
    localStorage.getItem("userEmail"),
    commodity,
    commodity === "Copper Wire" ? baseSKU : null,
  );

  const {
    data: businessData,
    isLoading: dataLoading,
    refreshDate,
    yoy_date,
    refetch,
  } = useBusinessCostData(
    commodity,
    localStorage.getItem("userEmail"),
    baseSKU,
    navItem,
    filters?.date?.[0]?.month_year ?? null,
  );
  const [businessDataCopy, setBusinessDataCopy] = useState<BusinessCostData[]>(
    [] as BusinessCostData[],
  );

  const {
    data: skuData,
    isLoading: skuLoading,
    // refetch: skuRefetch,
  } = useSKUAnalysisData(commodity, district, baseSKU, navItem);

  const [sortColumn, setSortColumn] = useState("");
  const [sortType, setSortType] = useState();
  const [tableData, setTableData] = useState<any>([]);

  const [showApprovalDialog, setShowApprovalDialog] = useState<boolean>(false);

  const [approvalComment, setApprovalComment] = useState<string>("");
  const [approvalCriteria, setApprovalCriteria] = useState<string>("self");
  const {
    mutate,
    mutateSku,
    isError,
    error,
    isSuccess,
    data: resdata,
  } = useUpdateBusinessCost();
  const { resetMutate, isResetLoading } = useResetBusinessCost();
  const [manualLoading, setManualLoading] = useState<boolean>(false);
  const [updateError, setUpdateError] = useState<{
    isError: boolean;
    message: string;
  }>({ isError: false, message: "" });
  const [districtForApproval, setDistrictForApproval] =
    useState<BusinessCostData>({} as BusinessCostData);
  const [isWirePopupOpen, setIsWirePopupOpen] = useState<boolean>(false);
  const [wireApprovalRow, setWireApprovalRow] =
    useState<BusinessCostData | null>(null);
  const [wirePopupRequest, setWirePopupRequest] = useState<{
    district: string;
    finalizedBoardCost: number;
    isRetained?: boolean;
  } | null>(null);
  const [isWireRetainFlow, setIsWireRetainFlow] = useState<boolean>(false);

  const [treeDistrict, SetTreeDistrict] = useState<string[]>([]);
  const [openModal, setOPenModal] = useState<boolean>(false);
  const [districtArray, setDistrictArray] = useState<string[]>([]);

  const {
    data: wirePopupItems,
    isLoading: isWirePopupItemsLoading,
    isError: isWirePopupItemsError,
  } = useOtherSKUsBC(
    wirePopupRequest?.district ?? null,
    wirePopupRequest?.finalizedBoardCost ?? null,
    wirePopupRequest !== null,
    wirePopupRequest?.isRetained,
  );

  const userDetails = useAppSelector((state) => {
    return state.user;
  });

  const getParentDistrictNames = (data: any) => {
    const grouped: any = [];

    data.forEach((item: any) => {
      if (item.district.indexOf(" - ") !== -1) {
        const groupKey = item.district.split(" - ")[0].trim();
        grouped.push(groupKey);
      }
    });
    SetTreeDistrict(grouped);
  };

  const screenStatus = useAppSelector((state) => {
    return state.disableScreens;
  });

  const BC_SETTING_TABS: BusinessCostTab[] = [
    {
      key: "benchmarkBoardSetting",
      label: "Benchmark Board Setting",
      trackingLabel: "Benchmark Board Setting",
    },
  ];

  const SKU_BOARD_ANALYSIS_TAB: BusinessCostTab = {
    key: "skuBoardAnalysis",
    label: "SKU Board Analysis",
    trackingLabel: "SKU Board Analysis",
  };

  const hasSkuBoardAnalysisAccess = Boolean(
    userDetails?.userModuleArray?.includes("SKU Board Analysis"),
  );

  const visibleTabs = isDimUser
    ? [SKU_BOARD_ANALYSIS_TAB]
    : hasSkuBoardAnalysisAccess
      ? [BC_SETTING_TABS[0], SKU_BOARD_ANALYSIS_TAB]
      : BC_SETTING_TABS;

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [queryKeys.businessCost] });

    if (isDimUser) {
      setNavItem(
        hiddenParam === undefined || hiddenParam === ""
          ? "skuBoardAnalysis"
          : hiddenParam,
      );
    } else {
      setNavItem(
        hiddenParam === undefined || hiddenParam === ""
          ? "benchmarkBoardSetting"
          : hiddenParam,
      );
    }
  }, []);

  useEffect(() => {
    setTableData(skuData || []);
  }, [skuData]);
  // useEffect(() => {
  //   skuRefetch();
  // }, [commodity]);

  const sortByColumnName = (
    data: any[],
    order: "asc" | "desc" | null,
    column: string,
  ) => {
    if (!order) return data;
    return [...data].sort((a, b) => {
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (columnName: string, sortType: any) => {
    const order: any = sortType ? (sortType === "asc" ? "desc" : null) : "asc";
    setSortColumn(columnName);
    setSortType(order);

    if (order) {
      setTableData(sortByColumnName(tableData, order, columnName));
    } else {
      setTableData(skuData); // original order
    }
  };

  const renderSortIcon = (field: any) => {
    const iconSize = 12;
    if (sortColumn === field && sortType === "asc") {
      return <img width={iconSize} height={iconSize} src={SortAsc} alt="asc" />;
    } else if (sortColumn === field && sortType === "desc") {
      return (
        <img width={iconSize} height={iconSize} src={SortDsc} alt="desc" />
      );
    } else {
      return (
        <img
          width={iconSize}
          height={iconSize}
          src={SortDefault}
          alt="default"
        />
      );
    }
  };

  useEffect(() => {
    if (
      filters &&
      filters?.commodity &&
      filters?.commodity?.length !== 0
      // &&
      // commodity === null
    ) {
      setCommodity(
        commodity || getDefaultCommodity(filters?.commodity) || null,
      );
      setDistrict(
        district
          ? filters?.districts && filters?.districts.length > 0
            ? filters?.districts.map((ele) => ele.district).includes(district)
              ? district
              : filters?.districts[0].district
            : district
          : filters?.districts && filters?.districts.length > 0
            ? filters?.districts[0].district
            : null,
      );
      setBaseSKU(
        baseSKU ||
          (filters?.baseSKU && filters?.baseSKU.length
            ? filters.baseSKU[0]?.base_sku
            : null),
      );
    }
  }, [filters, filters.baseSKU]);

  useEffect(() => {
    setBaseSKU(null);
  }, [commodity]);

  const validObj = (data: any) => {
    let variationFromEstimated =
      (data.finalized_business_cost - data.estimated_business_cost) /
      data.estimated_business_cost;
    let validation = {
      isValid: false,
      message: "",
    };
    /* Add validation that it cannot be less than vendor quotes */
    if (variationFromEstimated > 0.3) {
      validation = {
        isValid: false,
        message: Error_MESSAGE.CostTooHigh,
      };
    } else if (variationFromEstimated < -0.3) {
      validation = {
        isValid: false,
        message: Error_MESSAGE.CostTooLow,
      };
    } else if (data.finalized_business_cost < data.vendor_quote_max) {
      validation = {
        isValid: false,
        message: `${
          commodity?.toLowerCase() === "copper wire"
            ? Error_MESSAGE.wirePOCostValueMsg1
            : Error_MESSAGE.POCostValueMsg1
        } $${data?.vendor_quote_max?.toFixed(2)}, ${
          commodity?.toLowerCase() === "copper wire"
            ? Error_MESSAGE.wirePOCostValueMsg2
            : Error_MESSAGE.POCostValueMsg2
        }`,
      };
    } else {
      validation = {
        isValid: true,
        message: "",
      };
    }
    return validation;
  };

  useEffect(() => {
    if (businessData?.length > 0) {
      getParentDistrictNames(businessData);
      setBusinessDataCopy(
        JSON.parse(
          JSON.stringify(
            businessData.map((i) => {
              i.isEditable = false;
              i.validation = validObj(i);
              i.abs_adjusted_percent_change = Math.abs(
                i.adjusted_percent_change * 100,
              );
              return i;
            }),
          ),
        ),
      );
    }
  }, [businessData]);

  const getApprovalStatus = (
    statusCode: number | string | null | undefined,
  ) => {
    const normalizedStatusCode = Number(statusCode);
    switch (normalizedStatusCode) {
      case 0:
        return APPROVAL_STATUS.NoChange;
      case 1:
        return userDetails.role_name === USER_ROLE.Manager
          ? APPROVAL_STATUS.Requested
          : APPROVAL_STATUS.Pending;
      case 2:
        return APPROVAL_STATUS.Self;
      case 3:
        return APPROVAL_STATUS.Approved;
      default:
        return APPROVAL_STATUS.NoChange;
    }
  };

  useEffect(() => {
    if (resdata?.data) {
      // setManualLoading(true)
      if (isError) {
        setUpdateError({
          isError: true,
          message: "Some error occured. Please try again later !",
        });
      } else if (isSuccess) {
        let updatedRow = resdata.data.table_data[0];
        updatedRow.isEditable = false;
        updatedRow.validation = { isValid: true, message: "" };

        // Ensure approved_by and approved_on are set
        const currentUserName =
          localStorage?.getItem("name") ||
          userDetails?.userEmail ||
          localStorage.getItem("userEmail") ||
          "Unknown";
        const currentDate = new Date().toISOString().split("T")[0];
        updatedRow.approved_by = updatedRow.approved_by || currentUserName;
        updatedRow.approved_on = updatedRow.approved_on || currentDate;

        businessDataCopy.splice(
          businessDataCopy.findIndex(
            (value: BusinessCostData) => value.district === updatedRow.district,
          ),
          1,
          updatedRow,
        );
        setBusinessDataCopy([...businessDataCopy]);
        setShowApprovalDialog(false);
        refetch();
        // callback(true);
      }
      setTimeout(() => {
        setManualLoading(false);
      }, 300);
    }
  }, [resdata]);

  useEffect(() => {
    if (!wirePopupRequest || isWirePopupItemsLoading) {
      return;
    }

    if (isWirePopupItemsError) {
      toaster.push(
        <Message showIcon type={"error"} closable>
          <strong>Unable to fetch other SKU board cost details</strong>
        </Message>,
        { placement: "topCenter", duration: 5000 },
      );
      setWirePopupRequest(null);
      setWireApprovalRow(null);
      return;
    }

    setIsWirePopupOpen(true);
  }, [
    wirePopupRequest,
    isWirePopupItemsLoading,
    isWirePopupItemsError,
    toaster,
  ]);

  const buildApprovalPayload = (rowdata: BusinessCostData | null) => {
    let ogBsValue: any = businessData.filter(
      (data) => data.district === rowdata?.district,
    )[0]?.finalized_business_cost;
    let payload: BusinessCostUpdatePayload = {} as BusinessCostUpdatePayload;
    payload.table_data = rowdata ? [rowdata] : [districtForApproval];
    // Managers can no longer self-approve; every manager action raises an
    // approval request for a Lead to approve.
    payload.is_approval_request =
      userDetails.role_name === USER_ROLE.Manager
        ? true
        : approvalCriteria !== "self";
    payload.user_name = localStorage?.getItem("name") ?? "";
    if (userDetails.role_name === "Manager") {
      payload.isEditedByLead = false;
      if (
        districtForApproval &&
        districtForApproval?.previous_business_cost_finalized &&
        districtForApproval?.previous_business_cost_finalized !==
          districtForApproval?.finalized_business_cost
      ) {
        appInsights.trackEvent({
          name: `Board Cost: Finalized board cost changed from ${districtForApproval?.previous_business_cost_finalized} to ${districtForApproval?.finalized_business_cost}`,
        });
      }
    } else {
      let flag = false;
      if (
        (rowdata?.is_reviewed_flag === 1 || rowdata?.is_reviewed_flag === 2) &&
        ogBsValue !== rowdata.finalized_business_cost
      ) {
        flag = true;
        appInsights.trackEvent({
          name: `Board Cost: Finalized board cost changed from ${ogBsValue} to ${rowdata?.finalized_business_cost}`,
        });
      }
      payload.isEditedByLead = flag;
    }
    payload.requested_by =
      userDetails.userEmail /* || 'aibabbar@deloitte.com' */;
    payload.role_name = userDetails.role_name /* || 'Manager' */;
    payload.user_comments =
      approvalComment.length > 0
        ? approvalComment
        : rowdata?.user_comments || "";
    return payload;
  };

  const submitRequest = (rowdata: BusinessCostData | null) => {
    if (navItem === "skuBoardAnalysis") {
      setShowApprovalDialog(false);
      setApprovalComment("");
      toaster.push(
        <Message showIcon type={"info"} closable>
          <strong>
            SKU Board Analysis actions are local-only. No API call was made.
          </strong>
        </Message>,
        { placement: "topCenter", duration: 4000 },
      );
      return;
    }

    /* payload preparation for post */
    setManualLoading(true);
    mutate(buildApprovalPayload(rowdata));
    setApprovalComment("");
  };

  const handleSKUBoardCostApprove = (rowdata: any) => {
    const userEmail =
      userDetails?.userEmail || localStorage.getItem("userEmail") || "";
    const finalizedBoardCost = Number(
      rowdata?.finalizedBoardCost ?? rowdata?.finalized_business_cost ?? 0,
    );

    const isManager = userDetails?.role_name === USER_ROLE.Manager;

    const payload: BusinessCostSkuUpdatePayload = {
      user_name: userEmail,
      role_name: userDetails?.role_name || "",
      // Managers can no longer self-approve; they raise an approval request for
      // a Lead to approve. Leads still approve directly.
      is_approval_request: isManager ? true : false,
      isEditedByLead: isManager ? false : true,
      requested_by: userEmail,
      commodity: rowdata?.commodity || commodity || "",
      table_data: [
        {
          commodity: rowdata?.commodity || commodity || "",
          district: rowdata?.district || "",
          catalogue: rowdata?.catalogue || rowdata?.sku || "",
          base_sku: rowdata?.base_sku || baseSKU || "",
          finalized_board_cost_skus: finalizedBoardCost,
        },
      ],
    };

    setManualLoading(true);
    mutateSku(payload, {
      onSuccess: (res: any) => {
        const updatedRow: any =
          res?.data?.table_data?.[0] ??
          res?.data?.data?.table_data?.[0] ??
          null;

        setTableData(
          tableData.map((data: any) => {
            if (
              data.base_sku === rowdata.base_sku &&
              data.district === rowdata.district &&
              (data.sku || data.catalogue) ===
                (rowdata.sku || rowdata.catalogue)
            ) {
              const currentUserName =
                localStorage?.getItem("name") || userEmail || "Unknown";
              const currentDate = new Date().toISOString().split("T")[0];

              return {
                ...data,
                ...(updatedRow ?? {}),
                isEditable: false,
                finalizedBoardCost:
                  updatedRow?.finalized_board_cost_skus ??
                  updatedRow?.finalized_business_cost ??
                  finalizedBoardCost,
                is_reviewed_flag:
                  updatedRow?.is_reviewed_flag ?? data.is_reviewed_flag,
                approved_on: updatedRow?.approved_on ?? currentDate,
                approved_by: updatedRow?.approved_by ?? currentUserName,
                user_comments: updatedRow?.user_comments ?? data.user_comments,
              };
            }

            return data;
          }),
        );

        toaster.push(
          <Message showIcon type={"success"} closable>
            <strong>SKU Board Cost approved successfully.</strong>
          </Message>,
          { placement: "topCenter", duration: 4000 },
        );
      },
      onError: () => {
        toaster.push(
          <Message showIcon type={"error"} closable>
            <strong>Unable to approve SKU Board Cost. Please try again.</strong>
          </Message>,
          { placement: "topCenter", duration: 4000 },
        );
      },
      onSettled: () => {
        setManualLoading(false);
      },
    });
  };

  const shouldOpenWirePopup = (rowdata: BusinessCostData) => {
    return (
      commodity === "Copper Wire" &&
      (baseSKU === "THHN 12STR" || rowdata.base_sku === "THHN 12STR")
    );
  };

  const closeWirePopup = () => {
    setIsWirePopupOpen(false);
    setWireApprovalRow(null);
    setWirePopupRequest(null);
    setIsWireRetainFlow(false);
  };

  const submitWireApproval = (selectedItems: WireBenchmarkItem[]) => {
    if (!wireApprovalRow) {
      return;
    }

    const rowForSubmit: BusinessCostData = {
      ...wireApprovalRow,
      base_sku_details: selectedItems,
    };

    setDistrictForApproval(rowForSubmit);
    closeWirePopup();
    submitRequest(rowForSubmit);
  };

  const handleWireSubmit12STR = () => {
    if (!wireApprovalRow) {
      return;
    }

    const rowForSubmit: BusinessCostData = {
      ...wireApprovalRow,
      base_sku_details: [],
    };

    setDistrictForApproval(rowForSubmit);
    closeWirePopup();
    submitRequest(rowForSubmit);
  };

  const submitWireRetain = (selectedItems: WireBenchmarkItem[]) => {
    if (!wireApprovalRow) {
      return;
    }

    const rowForSubmit: BusinessCostData = {
      ...wireApprovalRow,
      base_sku_details: selectedItems,
    };

    setDistrictForApproval(rowForSubmit);
    closeWirePopup();
    submitRequest(rowForSubmit);
  };

  const handleWireRetain12STR = () => {
    if (!wireApprovalRow) {
      return;
    }

    const rowForSubmit: BusinessCostData = {
      ...wireApprovalRow,
      base_sku_details: [],
    };

    setDistrictForApproval(rowForSubmit);
    closeWirePopup();
    submitRequest(rowForSubmit);
  };

  const handleApproveClick = (rowdata: BusinessCostData) => {
    const rowForApproval: BusinessCostData = {
      ...rowdata,
      base_sku: rowdata.base_sku ?? baseSKU ?? undefined,
    };

    if (userDetails.role_name.toString() === USER_ROLE.Manager) {
      // Managers always raise an approval request (no self-approval).
      setApprovalCriteria("leadApproval");
    } else if (Math.abs(rowdata.adjusted_percent_change) <= 0) {
      setApprovalCriteria("self");
    } else {
      setApprovalCriteria("leadApproval");
    }

    setDistrictForApproval(rowForApproval);

    if (shouldOpenWirePopup(rowForApproval)) {
      setWireApprovalRow(rowForApproval);
      setIsWirePopupOpen(false);
      setWirePopupRequest({
        district: rowForApproval.district,
        finalizedBoardCost: Number(
          rowForApproval.finalized_business_cost.toFixed(2),
        ),
      });
      return;
    }

    if (userDetails.role_name.toString() === USER_ROLE.Manager) {
      setShowApprovalDialog(true);
    } else if (userDetails.role_name === USER_ROLE.Lead) {
      submitRequest(rowForApproval);
    } else {
      console.error(
        "The user doesn't have sufficient permissions to perform the operation",
      );
    }
  };

  /* Dialog Elements */
  const HeaderElement = (
    <div className="flex flex-row justify-between items-start">
      <div className="flex flex-col justify-start items-start gap-2">
        <span className="font-normal text-fs16 text-[#858585] h-[3vh]">
          Board Cost Setting
        </span>
        <span className="font-bold text-fs24">Cost Setting Approval</span>
      </div>
      <div>
        <img
          className="cursor-pointer"
          src={CloseIcon}
          onClick={() => {
            setShowApprovalDialog(false);
          }}
        ></img>
      </div>
    </div>
  );

  const FooterElement = (
    <div className="flex justify-end items-center gap-4">
      <Button
        label="Cancel"
        bgColor="#949494"
        size="md"
        onClick={() => {
          setShowApprovalDialog(false);
        }}
      ></Button>
      <Button
        label="Submit"
        bgColor="#86BC25"
        size="md"
        onClick={() => {
          submitRequest(null);
        }}
      ></Button>
    </div>
  );

  const handleFinalizedBusinessDownload = async (
    businessDataCopy: BusinessCostData[],
  ) => {
    if (businessDataCopy && businessDataCopy?.length > 0) {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Board Cost Setting");
      worksheet.columns = [
        { header: "Commodity", key: "commodity", width: 50 },
        { header: "District", key: "district", width: 50 },
        {
          header: `${
            hasFilterDate
              ? moment.utc(currentFilterDate).format("MMMM")
              : "No data available"
          } Finalized Board Cost ($/cft)`,
          key: "finalized_business_cost",
          width: 50,
        },
      ];

      const formattedData = businessDataCopy.map((businessDataObj) => ({
        district: businessDataObj.district,
        commodity: businessDataObj.commodity,
        finalized_business_cost: `$ ${businessDataObj.finalized_business_cost.toFixed(
          2,
        )}`,
      }));

      worksheet.addRows(formattedData);

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `Board Cost Setting.xlsx`);
    }
  };

  const handleApproveDisabled = (rowdata: any) => {
    if (
      (commodity && userDetails?.userCommArray?.includes(commodity)) === false
    ) {
      return true;
    } else {
      if (userDetails.role_name === "Lead") {
        return false;
      }
      // Managers can raise/re-raise approval repeatedly after editing inputs,
      // so their action buttons are never permanently disabled once a request
      // has been raised (is_reviewed_flag === 1).
      if (userDetails.role_name === USER_ROLE.Manager) {
        return false;
      }
      return !(
        rowdata.is_reviewed_flag === 0 ||
        rowdata.is_reviewed_flag === 3 ||
        rowdata.is_reviewed_flag === 2
      );
    }
  };

  const handleRetainClick = (rowdata: BusinessCostData) => {
    const rowForRetain: BusinessCostData = {
      ...rowdata,
      finalized_business_cost: rowdata.business_cost,
      base_sku: rowdata.base_sku ?? baseSKU ?? undefined,
      isRetained: 1,
      adjusted_percent_change: 0.0,
    };
    setApprovalCriteria("self");
    setApprovalComment("");
    setDistrictForApproval(rowForRetain);

    if (shouldOpenWirePopup(rowForRetain)) {
      setIsWireRetainFlow(true);
      setWireApprovalRow(rowForRetain);
      setIsWirePopupOpen(false);
      setWirePopupRequest({
        district: rowForRetain.district,
        finalizedBoardCost: Number(
          rowForRetain.finalized_business_cost.toFixed(2),
        ),
        isRetained: true,
      });
      return;
    }

    submitRequest(rowForRetain);
  };

  const handleEditDisabled = (rowdata: any) => {
    if (
      (commodity && userDetails?.userCommArray?.includes(commodity)) ===
        false &&
      !(userDetails.role_name === USER_ROLE.Lead) &&
      !(rowdata.is_reviewed_flag === 3)
    ) {
      return false;
    } else {
      if (userDetails.role_name === "Lead") {
        return true;
      }
      return (
        rowdata.is_reviewed_flag === 0 ||
        rowdata.is_reviewed_flag === 3 ||
        rowdata.is_reviewed_flag === 2
      );
    }
  };

  const handleSKULevelBCExcel = async () => {
    setIsSKUBoardDownload(true);
    const excelData = await getBoardCostReportDataForExcel(
      localStorage.getItem("userEmail"),
      commodity,
      "BoardCost",
      districtArray.length !== 0
        ? districtArray
        : filters?.districtforskuDownload
            .filter(
              (item: any): item is { district: string } => "district" in item,
            )
            .map((item) => item.district),
    );

    let newdata = excelData?.data?.data;
    let currentdate = new Date();
    if (newdata) {
      setIsSKUBoardDownload(false);

      // if (districtArray.length !== 0) {
      const disArray =
        districtArray.length !== 0
          ? [...districtArray]
          : [
              ...filters?.districtforskuDownload
                .filter(
                  (item: any): item is { district: string } =>
                    "district" in item,
                )
                .map((item) => item.district),
            ];

      if (disArray.length === 1) {
        let locationName = "";
        const workbook = new ExcelJS.Workbook();
        disArray?.forEach((item: any, index, arr) => {
          if (newdata[item]) {
            locationName = newdata[item] ? newdata[item][0].Location : "";
            const worksheet = workbook.addWorksheet(
              `${newdata[item][0].Location}`,
            );
            const Headers = [
              { header: "ConditionType", key: "ConditionType", width: 15 },
              { header: "VendorDCI", key: "VendorDCI", width: 15 },
              { header: "LocationType", key: "LocationType", width: 15 },
              { header: "Location", key: "Location", width: 15 },
              { header: "UPCNumber", key: "UPCNumber", width: 15 },
              {
                header: `${commodity === "Copper Wire" ? "Material" : "Catlog"}`,
                key: "Catlog",
                width: 15,
              },
              { header: "Price/Rate", key: "Price/Rate", width: 15 },
              { header: "UOM", key: "UOM", width: 15 },
              { header: "PrefixPurc", key: "PrefixPurc", width: 15 },
              { header: "ProductGroup", key: "ProductGroup", width: 15 },
              { header: "PriceGroup", key: "PriceGroup", width: 15 },
              { header: "DISC CODE", key: "DiscCode", width: 15 },
              { header: "VendorID", key: "VendorID", width: 15 },
              { header: "EffectiveDate", key: "EffectiveDate", width: 15 },
              { header: "ExpirationDate", key: "ExpirationDate", width: 15 },
              {
                header: "PublishedEffectiveDate",
                key: "PublishedEffectiveDate",
                width: 10,
              },
              {
                header: "PublishedExpirationDate",
                key: "PublishedExpirationDate",
                width: 10,
              },
            ];
            worksheet.columns = Headers;
            const formattedData = newdata[item].map((ele: any) => ({
              ConditionType: ele.ConditionType,
              VendorDCI: ele.VendorDCI,
              LocationType: ele.LocationType,
              Location: ele.Location,
              UPCNumber: ele.UPCNumber,
              Catlog: commodity === "Copper Wire" ? ele.MATERIAL : ele.CATLOG,
              "Price/Rate": Number(ele["Price/Rate"]),
              UOM: ele.UOM,
              PrefixPurc: ele.PrefixPurc,
              ProductGroup: ele.ProductGroup,
              PriceGroup: ele.PriceGroup,
              DiscCode: ele.DiscCode,
              VendorID: ele.VendorID,
              EffectiveDate: ele.EffectiveDate,
              ExpirationDate: ele.ExpirationDate,
              PublishedEffectiveDate: ele.PublishedEffectiveDate,
              PublishedExpirationDate: ele.PublishedExpirationDate,
            }));
            worksheet.addRows(formattedData);
            // Apply styling to all data rows
            worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
              row.eachCell({ includeEmpty: true }, (cell) => {
                cell.alignment = {
                  // wrapText: true,
                  vertical: "middle",
                  horizontal: rowNumber <= 1 ? "center" : "left",
                };
                cell.border = {
                  top: { style: "thin" },
                  left: { style: "thin" },
                  bottom: { style: "thin" },
                  right: { style: "thin" },
                };
                if (rowNumber <= 1) {
                  cell.font = { bold: true };
                  row.height = 30;
                }
              });
            });

            worksheet.columns.forEach((column: any) => {
              let maxLength = 0;
              column.eachCell({ includeEmpty: true }, (cell: any) => {
                if (cell.value) {
                  const cellValue = cell.value.toString();
                  maxLength = Math.max(maxLength, cellValue.length);
                }
              });
              column.width = maxLength + 2; // Add some padding to the width
            });
          } else {
            const worksheet = workbook.addWorksheet(`${item}`);
            worksheet.addRow(`No Data for ${item}`);
          }
        });

        const buffer = await workbook.xlsx.writeBuffer();

        saveAs(
          new Blob([buffer]),
          `${locationName ? `${locationName}_` : ""}ZRP_${
            commodity === "Steel Conduit"
              ? "TUBPA"
              : commodity === "PVC Conduit"
                ? "PVCPA"
                : "WYRPA"
          } BC Commodity Desk ${moment(new Date()).format("MM-DD-YYYY")}.xlsx`,
        );
        setDistrictArray([]);
        appInsights.trackEvent({
          name: "Board Cost: SKU Board Analysis",
          properties: {
            description: `${localStorage.getItem(
              "userEmail",
            )} has downloaded SKUBoardCostReport ${moment(new Date()).format(
              "MM-DD-YYYY",
            )}.xlsx File`,
          },
        });
      } else {
        let zip = new JSZip();
        const filePromises = disArray?.map(async (item: any, index, arr) => {
          const workbook = new ExcelJS.Workbook();
          if (newdata[item]) {
            const worksheet = workbook.addWorksheet(
              `${newdata[item][0].Location}`,
            );
            const Headers = [
              { header: "ConditionType", key: "ConditionType", width: 15 },
              { header: "VendorDCI", key: "VendorDCI", width: 15 },
              { header: "LocationType", key: "LocationType", width: 15 },
              { header: "Location", key: "Location", width: 15 },
              { header: "UPCNumber", key: "UPCNumber", width: 15 },
              {
                header: "Catlog",
                key: "Catlog",
                width: 15,
              },
              { header: "Price/Rate", key: "Price/Rate", width: 15 },
              { header: "UOM", key: "UOM", width: 15 },
              { header: "PrefixPurc", key: "PrefixPurc", width: 15 },
              { header: "ProductGroup", key: "ProductGroup", width: 15 },
              { header: "PriceGroup", key: "PriceGroup", width: 15 },
              { header: "DISC CODE", key: "DiscCode", width: 15 },
              { header: "VendorID", key: "VendorID", width: 15 },
              { header: "EffectiveDate", key: "EffectiveDate", width: 15 },
              { header: "ExpirationDate", key: "ExpirationDate", width: 15 },
              {
                header: "PublishedEffectiveDate",
                key: "PublishedEffectiveDate",
                width: 10,
              },
              {
                header: "PublishedExpirationDate",
                key: "PublishedExpirationDate",
                width: 10,
              },
            ];
            worksheet.columns = Headers;
            const formattedData = newdata[item].map((ele: any) => ({
              ConditionType: ele.ConditionType,
              VendorDCI: ele.VendorDCI,
              LocationType: ele.LocationType,
              Location: ele.Location,
              UPCNumber: ele.UPCNumber,
              Catlog: commodity === "Copper Wire" ? ele.MATERIAL : ele.CATLOG,
              "Price/Rate": Number(ele["Price/Rate"]),
              UOM: ele.UOM,
              PrefixPurc: ele.PrefixPurc,
              ProductGroup: ele.ProductGroup,
              PriceGroup: ele.PriceGroup,
              DiscCode: ele.DiscCode,
              VendorID: ele.VendorID,
              EffectiveDate: ele.EffectiveDate,
              ExpirationDate: ele.ExpirationDate,
              PublishedEffectiveDate: ele.PublishedEffectiveDate,
              PublishedExpirationDate: ele.PublishedExpirationDate,
            }));
            worksheet.addRows(formattedData);
            // Apply styling to all data rows
            worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
              row.eachCell({ includeEmpty: true }, (cell) => {
                cell.alignment = {
                  // wrapText: true,
                  vertical: "middle",
                  horizontal: rowNumber <= 1 ? "center" : "left",
                };
                cell.border = {
                  top: { style: "thin" },
                  left: { style: "thin" },
                  bottom: { style: "thin" },
                  right: { style: "thin" },
                };
                if (rowNumber <= 1) {
                  cell.font = { bold: true };
                  row.height = 30;
                }
              });
            });

            worksheet.columns.forEach((column: any) => {
              let maxLength = 0;
              column.eachCell({ includeEmpty: true }, (cell: any) => {
                if (cell.value) {
                  const cellValue = cell.value.toString();
                  maxLength = Math.max(maxLength, cellValue.length);
                }
              });
              column.width = maxLength + 2; // Add some padding to the width
            });
          }
          // else {
          //   const worksheet = workbook.addWorksheet(`${item}`);
          //   worksheet.addRow(`No Data for ${item}`);
          // }
          const buffer = await workbook.xlsx.writeBuffer();
          const excelBlob1 = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          if (newdata[item])
            zip.file(
              `${newdata[item][0].Location}_ZRP_${
                commodity === "Steel Conduit"
                  ? "TUBPA"
                  : commodity === "PVC Conduit"
                    ? "PVCPA"
                    : "WYRPA"
              } BC Commodity Desk ${moment(new Date()).format("MM-DD-YYYY")}.xlsx`,
              excelBlob1,
            );
        });

        // Wait for all promises to complete before generating the zip
        await Promise.all(filePromises);
        // Generate and save the zip file
        const zipBlob = await zip.generateAsync({ type: "blob" });

        saveAs(
          zipBlob,
          `ZRP_${
            commodity === "Steel Conduit"
              ? "TUBPA"
              : commodity === "PVC Conduit"
                ? "PVCPA"
                : "WYRPA"
          } BC Commodity Desk ${moment(new Date()).format("MM-DD-YYYY")}.zip`,
        );
        setDistrictArray([]);
        appInsights.trackEvent({
          name: "Board Cost: SKU Board Analysis",
          properties: {
            description: `${localStorage.getItem(
              "userEmail",
            )} has downloaded SKUBoardCostReport ${moment(new Date()).format(
              "MM-DD-YYYY",
            )}.xlsx File`,
          },
        });
      }
    } else {
      setIsSKUBoardDownload(false);
    }
  };

  const handleSKULevelBCExcelForWire = async () => {
    setIsSKUBoardDownload(true);
    if (districtArray.length !== 1) {
      setIsSKUBoardDownload(false);
      toaster.push(
        <Message showIcon type={"error"} closable>
          <strong>Only one district is allowed per request</strong>
        </Message>,
        { placement: "topCenter", duration: 5000 },
      );
      return;
    }

    const excelData = await getBoardCostReportDataForExcel(
      localStorage.getItem("userEmail"),
      commodity,
      "BoardCost",
      districtArray,
    );
    console.log(excelData, "download");
    let newdata = excelData?.data?.data;
    let currentdate = new Date();
    if (newdata) {
      setIsSKUBoardDownload(false);

      // if (districtArray.length !== 0) {
      const disArray = districtArray;

      if (disArray.length === 1) {
        let locationName = "";
        const workbook = new ExcelJS.Workbook();
        disArray?.forEach((item: any, index, arr) => {
          if (newdata[item]) {
            locationName = newdata[item] ? newdata[item][0].Location : "";
            const worksheet = workbook.addWorksheet(
              `${newdata[item][0].Location}`,
            );
            const Headers = [
              { header: "ConditionType", key: "ConditionType", width: 15 },
              { header: "LocationType", key: "LocationType", width: 15 },
              { header: "Location", key: "Location", width: 15 },
              { header: "VendorID", key: "VendorID", width: 15 },
              {
                header: "Material",
                key: "Catlog",
                width: 15,
              },
              { header: "UPCNumber", key: "UPCNumber", width: 15 },
              { header: "Description", key: "Desc", width: 15 },
              { header: "Rate", key: "Price/Rate", width: 15 },
              { header: "UOM", key: "UOM", width: 15 },
              { header: "EffectiveDate", key: "EffectiveDate", width: 15 },
              { header: "ExpirationDate", key: "ExpirationDate", width: 15 },
              {
                header: "PublishedEffectiveDate",
                key: "PublishedEffectiveDate",
                width: 10,
              },
              {
                header: "PublishedExpirationDate",
                key: "PublishedExpirationDate",
                width: 10,
              },
            ];
            worksheet.columns = Headers;
            const formattedData = newdata[item].map((ele: any) => ({
              ConditionType: ele.ConditionType,
              LocationType: ele.LocationType,
              Location: ele.Location,
              VendorID: ele.VendorID,
              Catlog: ele.MATERIAL,
              UPCNumber: ele.UPCNumber,
              Desc: ele.PrefixPurc,
              "Price/Rate": Number(ele["Price/Rate"]),
              UOM: ele.UOM,
              EffectiveDate: ele.EffectiveDate,
              ExpirationDate: ele.ExpirationDate,
              PublishedEffectiveDate: ele.PublishedEffectiveDate,
              PublishedExpirationDate: ele.PublishedExpirationDate,
            }));
            worksheet.addRows(formattedData);
            // Apply styling to all data rows
            worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
              row.eachCell({ includeEmpty: true }, (cell) => {
                cell.alignment = {
                  // wrapText: true,
                  vertical: "middle",
                  horizontal: rowNumber <= 1 ? "center" : "left",
                };
                cell.border = {
                  top: { style: "thin" },
                  left: { style: "thin" },
                  bottom: { style: "thin" },
                  right: { style: "thin" },
                };
                if (rowNumber <= 1) {
                  cell.font = { bold: true };
                  row.height = 30;
                }
              });
            });

            worksheet.columns.forEach((column: any) => {
              let maxLength = 0;
              column.eachCell({ includeEmpty: true }, (cell: any) => {
                if (cell.value) {
                  const cellValue = cell.value.toString();
                  maxLength = Math.max(maxLength, cellValue.length);
                }
              });
              column.width = maxLength + 2; // Add some padding to the width
            });
          } else {
            const worksheet = workbook.addWorksheet(`${item}`);
            worksheet.addRow(`No Data for ${item}`);
          }
        });

        const buffer = await workbook.xlsx.writeBuffer();

        saveAs(
          new Blob([buffer]),
          `${locationName ? `${locationName}_` : ""}ZRP_${
            commodity === "Steel Conduit"
              ? "TUBPA"
              : commodity === "PVC Conduit"
                ? "PVCPA"
                : "WYRPA"
          } BC Commodity Desk ${moment(new Date()).format("MM-DD-YYYY")}.xlsx`,
        );
        setDistrictArray([]);
        appInsights.trackEvent({
          name: "Board Cost: SKU Board Analysis",
          properties: {
            description: `${localStorage.getItem(
              "userEmail",
            )} has downloaded SKUBoardCostReport ${moment(new Date()).format(
              "MM-DD-YYYY",
            )}.xlsx File`,
          },
        });
      } else {
        let zip = new JSZip();
        const filePromises = disArray?.map(async (item: any, index, arr) => {
          const workbook = new ExcelJS.Workbook();
          if (newdata[item]) {
            const worksheet = workbook.addWorksheet(
              `${newdata[item][0].Location}`,
            );
            const Headers = [
              { header: "ConditionType", key: "ConditionType", width: 15 },
              { header: "LocationType", key: "LocationType", width: 15 },
              { header: "Location", key: "Location", width: 15 },
              { header: "VendorID", key: "VendorID", width: 15 },
              {
                header: "Material",
                key: "Catlog",
                width: 15,
              },
              { header: "UPCNumber", key: "UPCNumber", width: 15 },
              { header: "Description", key: "Desc", width: 15 },
              { header: "Rate", key: "Price/Rate", width: 15 },
              { header: "UOM", key: "UOM", width: 15 },
              { header: "EffectiveDate", key: "EffectiveDate", width: 15 },
              { header: "ExpirationDate", key: "ExpirationDate", width: 15 },
              {
                header: "PublishedEffectiveDate",
                key: "PublishedEffectiveDate",
                width: 10,
              },
              {
                header: "PublishedExpirationDate",
                key: "PublishedExpirationDate",
                width: 10,
              },
            ];
            worksheet.columns = Headers;
            const formattedData = newdata[item].map((ele: any) => ({
              ConditionType: ele.ConditionType,
              LocationType: ele.LocationType,
              Location: ele.Location,
              VendorID: ele.VendorID,
              Catlog: ele.MATERIAL,
              UPCNumber: ele.UPCNumber,
              Desc: ele.PrefixPurc,
              "Price/Rate": Number(ele["Price/Rate"]),
              UOM: ele.UOM,
              EffectiveDate: ele.EffectiveDate,
              ExpirationDate: ele.ExpirationDate,
              PublishedEffectiveDate: ele.PublishedEffectiveDate,
              PublishedExpirationDate: ele.PublishedExpirationDate,
            }));
            worksheet.addRows(formattedData);
            // Apply styling to all data rows
            worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
              row.eachCell({ includeEmpty: true }, (cell) => {
                cell.alignment = {
                  // wrapText: true,
                  vertical: "middle",
                  horizontal: rowNumber <= 1 ? "center" : "left",
                };
                cell.border = {
                  top: { style: "thin" },
                  left: { style: "thin" },
                  bottom: { style: "thin" },
                  right: { style: "thin" },
                };
                if (rowNumber <= 1) {
                  cell.font = { bold: true };
                  row.height = 30;
                }
              });
            });

            worksheet.columns.forEach((column: any) => {
              let maxLength = 0;
              column.eachCell({ includeEmpty: true }, (cell: any) => {
                if (cell.value) {
                  const cellValue = cell.value.toString();
                  maxLength = Math.max(maxLength, cellValue.length);
                }
              });
              column.width = maxLength + 2; // Add some padding to the width
            });
          }
          // else {
          //   const worksheet = workbook.addWorksheet(`${item}`);
          //   worksheet.addRow(`No Data for ${item}`);
          // }
          const buffer = await workbook.xlsx.writeBuffer();
          const excelBlob1 = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          if (newdata[item])
            zip.file(
              `${newdata[item][0].Location}_ZRP_${
                commodity === "Steel Conduit"
                  ? "TUBPA"
                  : commodity === "PVC Conduit"
                    ? "PVCPA"
                    : "WYRPA"
              } BC Commodity Desk ${moment(new Date()).format("MM-DD-YYYY")}.xlsx`,
              excelBlob1,
            );
        });

        // Wait for all promises to complete before generating the zip
        await Promise.all(filePromises);
        // Generate and save the zip file
        const zipBlob = await zip.generateAsync({ type: "blob" });

        saveAs(
          zipBlob,
          `ZRP_${
            commodity === "Steel Conduit"
              ? "TUBPA"
              : commodity === "PVC Conduit"
                ? "PVCPA"
                : "WYRPA"
          } BC Commodity Desk ${moment(new Date()).format("MM-DD-YYYY")}.zip`,
        );
        setDistrictArray([]);
        appInsights.trackEvent({
          name: "Board Cost: SKU Board Analysis",
          properties: {
            description: `${localStorage.getItem(
              "userEmail",
            )} has downloaded SKUBoardCostReport ${moment(new Date()).format(
              "MM-DD-YYYY",
            )}.xlsx File`,
          },
        });
      }
    } else {
      setIsSKUBoardDownload(false);
    }
  };

  const handleBCRecommendationsExcel = async () => {
    const excelData = await getRecommendedBCDataForExcel(
      localStorage.getItem("userEmail"),
      commodity,
      baseSKU,
    );

    let newdata = excelData?.data?.data;
    let currentdate = new Date();

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(`Board Cost Recommendation_sheet`);

    // Temporarily hidden from the export per business request; keeping the
    // column definition/data behind this flag so it can be re-enabled easily.
    const SHOW_YOY_SALES_COLUMN = false;

    const Headers = [
      { header: "District", key: "District", width: 15 },
      {
        header: "Current Board Cost ($/cft)",
        key: "CurrentBoardCost",
        width: 15,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: `${
          commodity !== "Copper Wire"
            ? hasFilterDate
              ? moment.utc(currentFilterDate).format("MMMM")
              : "No data available"
            : ""
        } Calculated Recommendation ($/cft)`,
        key: "RecommendedBoardCost",
        width: 15,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: `${
          commodity !== "Copper Wire"
            ? hasFilterDate
              ? moment.utc(currentFilterDate).format("MMMM")
              : "No data available"
            : ""
        } Finalized Board Cost ($/cft)`,
        key: "FinalizedBoardCost",
        width: 15,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: "Current vs Recommended % Variance",
        key: "CurrentvsRecommendedVariance",
        width: 15,
        style: { numFmt: "0.00%" },
      },
      ...(SHOW_YOY_SALES_COLUMN
        ? [
            {
              header: `YoY ${
                commodity !== "Copper Wire"
                  ? moment.utc(yoy_date).format("MMM")
                  : ""
              } Sales lbs.`,
              key: "YoYSaleslbs",
              width: 15,
              style: { numFmt: "0.00%" },
            },
          ]
        : []),
      {
        header: `${
          commodity !== "Copper Wire"
            ? hasFilterDate
              ? moment.utc(currentFilterDate).format("MMM")
              : "No data available"
            : ""
        } Projected Avg. PO Cost`,
        key: "ProjectedAvgPOCost",
        width: 15,
        style: { numFmt: "$#,##0.00" },
      },
      {
        header: "Recommended BC Markup % over PO",
        key: "RecommendedBCMarkupoverPO",
        width: 15,
        style: { numFmt: "0.00%" },
      },
    ];
    worksheet.columns = Headers;
    const finalizedByDistrict = new Map(
      (businessData ?? []).map((d: any) => [
        d.district,
        d.finalized_business_cost,
      ]),
    );
    const estimatedByDistrict = new Map(
      (businessData ?? []).map((d: any) => [
        d.district,
        d.estimated_business_cost,
      ]),
    );
    const formattedData = newdata.map((ele: any) => ({
      District: ele.district,
      CurrentBoardCost: ele.CurrentBoardCost,
      RecommendedBoardCost:
        estimatedByDistrict.get(ele.district) ?? ele.RecommendedBoardCost,
      FinalizedBoardCost: finalizedByDistrict.get(ele.district) ?? null,
      CurrentvsRecommendedVariance: ele.CurrentvsRecommendedVariance / 100,
      YoYSaleslbs: ele.YoYSaleslbs / 100,
      ProjectedAvgPOCost: ele.ProjectedAvgPOCost,
      RecommendedBCMarkupoverPO: ele.RecommendedBCMarkupoverPO / 100,
    }));
    worksheet.addRows(formattedData);
    // Apply styling to all data rows
    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.alignment = {
          // wrapText: true,
          vertical: "middle",
          horizontal: rowNumber <= 1 ? "center" : "left",
        };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        if (rowNumber <= 1) {
          cell.font = { bold: true };
        }
      });
    });

    worksheet.columns.forEach((column: any) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell: any) => {
        if (cell.value) {
          const cellValue = cell.value.toString();
          maxLength = Math.max(maxLength, cellValue.length);
        }
      });
      column.width = maxLength + 2; // Add some padding to the width
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Board Cost Recommendation_${commodity}.xlsx`);
    appInsights.trackEvent({
      name: "Board Cost: Board cost Recommendation",
      properties: {
        description: `${localStorage.getItem(
          "userEmail",
        )} has downloaded "Board Cost Recommendation_${commodity}.xlsx" Report`,
      },
    });
  };

  const handleNewDownloadExcel = async () => {
    setIsSKUBoardDownload(true);

    try {
      // Fetch fresh data for ALL base SKUs — commodity & district stay as selected.
      // Passing baseSKU as null makes getSKUAnalysisData skip the base_sku query
      // param entirely, which returns data for all base SKUs.
const excelResponse: any = await getSKUAnalysisData(commodity, district, "All");
      const allBaseSkuData: any[] = excelResponse?.data?.data?.data ?? [];

      if (!allBaseSkuData || allBaseSkuData.length === 0) {
        toaster.push(
          <Message showIcon type={"info"} closable>
            <strong>No data available to export.</strong>
          </Message>,
          { placement: "topCenter", duration: 4000 },
        );
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("SKU_Board_Analysis_Details");

      //adding filters
      worksheet.addRow(["Commodity:", commodity]);
      worksheet.addRow(["District:", district]);
      worksheet.addRow(["Base SKU", "All"]);
      worksheet.addRow([
        "Month",
        hasFilterDate
          ? moment.utc(currentFilterDate).format("MMMM")
          : "No data available",
      ]);
      worksheet.addRow([]); // Empty row

      //adding headers
      worksheet.addRow([
        "Base SKU",
        "SKU",
        `${
          commodity === "Copper Wire"
            ? "Current"
            : hasFilterDate
              ? moment.utc(currentFilterDate).subtract(1, "month").format("MMMM")
              : "No data available"
        } PO Cost`,
        `${
          commodity !== "Copper Wire"
            ? hasFilterDate
              ? moment.utc(currentFilterDate).format("MMMM")
              : "No data available"
            : ""
        } Expected PO Cost ($/cft)`,
        "Current Board Cost ($/cft)",
        "Expected PPV with Current BC",
        "Finalized Board Cost ($/cft)",
        "Expected PPV with Finalized BC",
        "Finalized vs Current Variance",
      ]);

      //adding data
      worksheet.addRows([
        ...allBaseSkuData.map((ele: any) => {
          const finalizedVsCurrentVariance = getFinalizedVsCurrentVariance(ele);
          const varianceValue =
            finalizedVsCurrentVariance !== null &&
            Number.isFinite(finalizedVsCurrentVariance)
              ? finalizedVsCurrentVariance / 100
              : "";
        const currentBoardCostPercentageValue =
          typeof ele.currentBoardCostPercentage === "number" &&
          Number.isFinite(ele.currentBoardCostPercentage)
            ? ele.currentBoardCostPercentage / 100
            : "";
        const finalizedBoardCostPercentageValue =
          typeof ele.finalizedBoardCostPercentage === "number" &&
          Number.isFinite(ele.finalizedBoardCostPercentage)
            ? ele.finalizedBoardCostPercentage / 100
            : "";
            
          return [
            ele.base_sku ?? "-",
            ele.sku,
            ele.previousMonthPoCost,
            ele.currentExpectedPoCost,
            ele.currentBoardCost,
            ele.currentBoardCostPercentage / 100,
            ele.finalizedBoardCost,
            ele.finalizedBoardCostPercentage / 100,
            varianceValue,
          ];
        }),
      ]);

      // Apply styling to all data rows
      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        if (rowNumber < 5) {
          row.getCell(1).font = { bold: true };
          row.eachCell({ includeEmpty: true }, (cell) => {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        } else {
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            if (rowNumber === 6) {
              row.font = { bold: true };
            }
            cell.alignment = {
              vertical: "middle",
              horizontal:
                rowNumber === 6 ? "center" : colNumber === 1 ? "left" : "right",
            };
            if ([3, 4, 5, 7].includes(colNumber)) {
              cell.numFmt = "$#,##0.00";
            }
            if (
              [6, 8, 9].includes(colNumber) &&
              typeof cell.value === "number" &&
              Number.isFinite(cell.value)
            ) {
              cell.numFmt = "0.0%";
            }
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        }
      });

      worksheet.columns.forEach((column: any) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell: any) => {
          if (cell.value) {
            const cellValue = cell.value.toString();
            maxLength = Math.max(maxLength, cellValue.length);
          }
        });
        column.width = maxLength + 2;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer]),
        `SKU Board Cost Analysis Details_${commodity}_${district}.xlsx`,
      );
      appInsights.trackEvent({
        name: "Board Cost: SKU Board Cost Analysis Details",
        properties: {
          description: `${localStorage.getItem(
            "userEmail",
          )} has downloaded "SKU Board Cost Analysis Details_${commodity}_${district}.xlsx" Report`,
        },
      });
    } catch (err) {
      toaster.push(
        <Message showIcon type={"error"} closable>
          <strong>Unable to export data. Please try again.</strong>
        </Message>,
        { placement: "topCenter", duration: 4000 },
      );
    } finally {
      setIsSKUBoardDownload(false);
    }
  };

  const handleTabonClick = (tabName: string, tabLabel: string) => {
    setNavItem(tabName);
    if (tabName === "skuBoardAnalysis") {
      setDistrict(null);
    }
    trackTabClick(tabLabel, "Board cost Settings");
  };

  // Reset finalized board cost back to the calculated recommendation and move
  // rows back to "Review Rec". `scope` decides how wide the reset is applied:
  //  - "commodity": the selected base SKU of the currently selected commodity
  //    (sent to the API as scope "Base_sku" along with the base_sku value).
  //  - "all": every base SKU/district of the currently selected commodity
  //    (sent to the API with scope "commodity").
  const runReset = (scope: "commodity" | "all") => {
    const isBaseSkuScope = scope === "commodity";
    const payload: BusinessCostResetPayload = {
      scope: isBaseSkuScope ? "Base_sku" : "commodity",
      commodity: commodity ?? null,
      ...(isBaseSkuScope && { base_sku: baseSKU }),
      requested_by:
        userDetails.userEmail || localStorage.getItem("userEmail") || "",
      user_name: localStorage?.getItem("name") ?? "",
      role_name: userDetails.role_name || "",
    };

    resetMutate(payload, {
      onSuccess: () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: [queryKeys.businessCost] });
        toaster.push(
          <Message showIcon type={"success"} closable>
            <strong>Board cost reset successfully.</strong>
          </Message>,
          { placement: "topCenter", duration: 4000 },
        );
      },
      onError: () => {
        toaster.push(
          <Message showIcon type={"error"} closable>
            <strong>Unable to reset board cost. Please try again.</strong>
          </Message>,
          { placement: "topCenter", duration: 4000 },
        );
      },
    });
  };

  const handleReset = () => runReset("commodity");

  const handleResetAll = () => runReset("all");

  const handleRowReset = (rowdata: BusinessCostData) => {
    const payload: BusinessCostResetPayload = {
      scope: "district",
      commodity: commodity ?? null,
      district: rowdata.district,
      requested_by:
        userDetails.userEmail || localStorage.getItem("userEmail") || "",
      user_name: localStorage?.getItem("name") ?? "",
      role_name: userDetails.role_name || "",
    };

    resetMutate(payload, {
      onSuccess: () => {
        refetch();
        queryClient.invalidateQueries({ queryKey: [queryKeys.businessCost] });
        toaster.push(
          <Message showIcon type={"success"} closable>
            <strong>Board cost reset successfully.</strong>
          </Message>,
          { placement: "topCenter", duration: 4000 },
        );
      },
      onError: () => {
        toaster.push(
          <Message showIcon type={"error"} closable>
            <strong>Unable to reset board cost. Please try again.</strong>
          </Message>,
          { placement: "topCenter", duration: 4000 },
        );
      },
    });
  };

  const getCommitmentActualPurchasePace = (value: number) => {
    switch (true) {
      case value < 0:
        return (
          <div className="flex items-center">
            <div className="pl-[0.5vh]">
              <img
                className="rotate-180 w-[0.75vw] h-[0.75vw]"
                src={DangerArrowUpSm}
                alt="danger"
              />
            </div>
          </div>
        );
      case value >= 0 && value <= 1.5:
        return (
          <div className="flex items-center">
            <div className="pl-[0.5vh]">
              <img
                className="w-[0.75vw] h-[0.75vw]"
                src={WarnArrowUpSm}
                alt="warn"
              />
            </div>
          </div>
        );
      case value > 1.5:
        return (
          <div className="flex items-center">
            <div className="pl-[0.5vh]">
              <img
                className="w-[0.75vw] h-[0.75vw]"
                src={SafeArrowDownSm}
                alt="safe"
              />
            </div>
          </div>
        );

      default:
        return <>{value}</>;
    }
  };

  const trunc = (x: any) => {
    return (Math.trunc(x * 100) / 100).toFixed(2);
    // const [i, d = ""] = String(x).split(".");
    // return `${i}.${(d + "0")[0]}`; // always 1 decimal
  };

  const getFinalizedVsCurrentVariance = (rowdata: any) => {
    const currentBoardCost = Number(rowdata?.currentBoardCost);
    const finalizedBoardCost = Number(rowdata?.finalizedBoardCost);

    if (
      !Number.isFinite(currentBoardCost) ||
      !Number.isFinite(finalizedBoardCost) ||
      currentBoardCost === 0
    ) {
      return null;
    }

    return ((finalizedBoardCost - currentBoardCost) / currentBoardCost) * 100;
  };

  const highlightedSkuRows = new Set([
    "EMT '3/4",
    "PVC 40 '4",
    "BARE-Stranded-4/0 AWG",
    "NM-WG-10 AWG-3-C",
    "XHHW-2-Stranded-600 MCM",
    "THHN-Stranded-500 MCM",
    "XLP-USE-Stranded-6 AWG",
    "THHN-Stranded-12 AWG",
  ]);

  const isHighlightedSkuRow = (sku?: string) =>
    highlightedSkuRows.has(sku ?? "");

  const shouldShowDashForValue = (value: unknown) => {
    if (value === null || value === undefined || value === "") {
      return true;
    }

    const numericValue = Number(value);
    return Number.isNaN(numericValue) || numericValue === 0;
  };

  const formatKpiValue = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return "-";
    }
    return commodity?.toLowerCase() === "copper wire"
      ? `$${formatNumberwithCommas(value, true)}`
      : numberFormatter(value, 2, "$");
  };

  const KPI_BREAKDOWN_LABELS: Array<{
    key: "agent_pricing" | "vendor_quotes" | "target_cost" | "project_pricing";
    label: string;
  }> = [
    { key: "agent_pricing", label: "Market Pricing" },
    { key: "vendor_quotes", label: "Supplier Quotes" },
    { key: "target_cost", label: "Index-Implied Cost" },
    { key: "project_pricing", label: "Project Pricing" },
  ];

  const renderBoardCostEstimateTooltip = (rowdata: BusinessCostData) => {
    const kpi = rowdata.kpi;
    const values = kpi?.table_data?.[0];
    const weights = kpi?.table_data?.[1];

    if (!kpi || !values) {
      return null;
    }

    const columns = KPI_BREAKDOWN_LABELS.filter(
      ({ key }) => values[key] !== null && values[key] !== undefined,
    );

    if (columns.length === 0) {
      return null;
    }

    return (
      <div className="min-w-[16vw] text-black">
        <div className="font-bold text-[1.35vh] mb-[1vh] whitespace-nowrap">
          {`Board Cost Estimate — ${kpi.district}`}
        </div>
        <div className="flex justify-between items-start gap-[1vw] mb-[0.4vh]">
          {columns.map(({ key, label }) => (
            <div
              key={`label-${key}`}
              className="flex-1 text-[#8C8C8C] font-semibold text-[1.1vh] whitespace-nowrap"
            >
              {label}
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center gap-[1vw] bg-[#EEF7E3] rounded-[4px] py-[0.7vh] px-[0.5vw] mb-[0.4vh]">
          {columns.map(({ key }) => (
            <div
              key={`value-${key}`}
              className="flex-1 font-bold text-[1.3vh] whitespace-nowrap"
            >
              {formatKpiValue(values[key])}
            </div>
          ))}
        </div>
        <div className="flex justify-between items-center gap-[1vw] mb-[1vh]">
          {columns.map(({ key }) => (
            <div
              key={`weight-${key}`}
              className="flex-1 text-[#8C8C8C] text-[1.1vh] whitespace-nowrap"
            >
              {weights?.[key] !== null && weights?.[key] !== undefined
                ? `${weights[key]}%`
                : "-"}
            </div>
          ))}
        </div>
        <div className="border-t border-gray-200 pt-[0.8vh] flex justify-between items-center gap-[1vw]">
          <span className="font-bold text-[1.2vh] whitespace-nowrap">
            Recommended Board Cost
          </span>
          <span className="text-[1.5vh] whitespace-nowrap">
            {formatKpiValue(kpi.recommended_business_cost)}
          </span>
        </div>
      </div>
    );
  };

  // Wraps a cell's rendered content with the "Board Cost Estimate" hover
  // popover (used on every benchmarkBoardSetting column body except Action
  // and Approval Status). Falls back to the unwrapped content when the row
  // has no `kpi` breakdown to show.
  const withKpiHoverTooltip = (
    rowdata: BusinessCostData,
    children: React.ReactNode,
  ) => {
    const kpiTooltipContent = renderBoardCostEstimateTooltip(rowdata);
    if (!kpiTooltipContent) {
      return children;
    }
    return (
      <Whisper
        trigger="hover"
        placement="auto"
        speaker={<Popover>{kpiTooltipContent}</Popover>}
      >
        <div className="w-full h-full">{children}</div>
      </Whisper>
    );
  };

  const currentFilterDate = filters?.date?.[0]?.month_year ?? null;
  const hasFilterDate = Boolean(currentFilterDate);

  return (
    <>
      {isLoading || dataLoading || skuLoading || manualLoading || isResetLoading ? (
        <Loader
          backdrop
          content="Loading..."
          size="md"
          vertical
          className="text-black font-bold"
          style={{ opacity: 0.6, zIndex: 1102 }}
        />
      ) : (
        <div className="flex flex-col justify-stretch items-stretch h-full min-w-0 max-w-full business-cost-settings overflow-x-hidden">
          <div className="grow shrink-0 basis-[20%] ps-[3%] pr-[5%] pt-[1.6%] flex flex-col flex-nowrap justify-start items-start gap-0 shadow-normal relative">
            <div className="flex justify-between items-center w-full">
              <div className="font-normal text-[2vw]">Board Cost Setting</div>
              <div className="flex justify-start items-center gap-8">
                <div className="flex flex-col justify-start items-start">
                  <label className="font-bold text-[0.8vw]">Commodity</label>
                  <SelectPicker
                    data={sortCommodityFirst(filters.commodity)}
                    value={commodity ?? getDefaultCommodity(filters.commodity)}
                    labelKey="commodity"
                    valueKey="commodity"
                    style={{ width: width }}
                    onChange={(value: string | null) => {
                      setBaseSKU(null);
                      setCommodity(value);
                    }}
                    placeholder="All"
                    cleanable={false}
                  ></SelectPicker>
                </div>
                {navItem === "skuBoardAnalysis" && (
                  <div className="flex flex-col justify-start items-start">
                    <label className="font-bold text-[0.8vw]">District</label>
                    <SelectPicker
                      data={filters.districts}
                      value={district ?? filters?.districts?.[0].district}
                      labelKey="district"
                      valueKey="district"
                      style={{ width: width }}
                      onChange={(value: string | null) => {
                        setDistrict(value);
                      }}
                      // placeholder="All"
                      cleanable={false}
                    ></SelectPicker>
                  </div>
                )}

                <div className="flex flex-col justify-start items-start">
                  <label className="font-bold text-[0.8vw]">Base SKU</label>
                  <SelectPicker
                    data={filters?.baseSKU?.filter((ele) => ele.base_sku)}
                    value={baseSKU}
                    labelKey="base_sku"
                    valueKey="base_sku"
                    style={{ width: width }}
                    onChange={(value: string | null) => {
                      setBaseSKU(value);
                    }}
                    placeholder="All"
                    cleanable={false}
                  ></SelectPicker>
                </div>
              </div>
            </div>
            <span className="font-bold text-fs16 text-[#858585]">
              Negotiation {commodity === "Copper Wire" ? "Week" : "Month"}
              :&nbsp;
              <span className="font-bold underline text-deloitteGreen">
                {hasFilterDate
                  ? commodity === "Copper Wire"
                    ? convertDateToFullDate(currentFilterDate)
                    : convertDateToMonthYear(currentFilterDate)
                  : "No data available"}
              </span>
            </span>
            <div className="absolute bottom-0 flex justify-start items-center text-black font-normal text-[1.8vh]">
              <div className="flex justify-start items-center gap-6">
                {/* <span
                  className={
                    (navItem === "benchmarkBoardSetting"
                      ? `font-bold border-b-2 border-black ${
                      // disableQuote
                      //   ? "text-gray-400 cursor-not-allowed"
                      // :
                      "cursor-pointer"
                      }`
                      : `${
                      // disableQuote
                      //   ? "text-gray-400 cursor-not-allowed"
                      // :
                      "cursor-pointer"
                      }`) + " pb-[1vh]"
                  }
                  onClick={() => {
                    // if (disableQuote) return;
                    handleTabonClick(
                      "benchmarkBoardSetting",
                      "Benchmark Board Setting",
                    );
                  }}
                >
                  Benchmark Board Setting
                </span> */}

                {visibleTabs.map((tab) => (
                  <span
                    key={tab.key}
                    className={`cursor-pointer pb-[1vh] ${
                      navItem === tab.key
                        ? "font-bold border-b-2 border-black"
                        : ""
                    }`}
                    onClick={() => handleTabonClick(tab.key, tab.trackingLabel)}
                  >
                    {tab.label}
                  </span>
                ))}
                {/* <span
                  className={
                    (navItem === "skuBoardAnalysis"
                      ? "font-bold border-b-2 border-black"
                      : "") + " cursor-pointer pb-[1vh]"
                  }
                  onClick={() =>
                    handleTabonClick("skuBoardAnalysis", "SKU Board Analysis")
                  }
                >
                  SKU Board Analysis
                </span> */}
              </div>
            </div>
            {/* {navItem === "skuBoardAnalysis" && ( */}
            <div className="absolute bottom-2 right-[5%] flex gap-10">
              {navItem === "skuBoardAnalysis" && (
                <div className="flex justify-end gap-5">
                  <div
                    onClick={() => {
                      setOPenModal(true);
                    }}
                    className={
                      "bottom-2 text-white bold text-[0.8vw] right-[5.3vh] bg-[#86BC25] p-[0.4vw] rounded-md cursor-pointer flex items-center"
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="1vw"
                      height="1vw"
                      viewBox="0 0 12 16"
                      fill="none"
                    >
                      <path
                        d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                        fill="white"
                      />
                    </svg>
                    <span className="ml-[3px]">SKU Level Board Cost</span>
                  </div>
                  <div
                    onClick={() => {
                      handleNewDownloadExcel();
                    }}
                    className={
                      "bottom-2 text-white bold text-[0.8vw] right-[5.3vh] bg-[#86BC25] p-[0.4vw] rounded-md cursor-pointer flex items-center"
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="1vw"
                      height="1vw"
                      viewBox="0 0 12 16"
                      fill="none"
                    >
                      <path
                        d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                        fill="white"
                      />
                    </svg>
                    <span className="ml-[3px]">Export Data</span>
                  </div>
                </div>
              )}
              {navItem === "benchmarkBoardSetting" && (
                <div className="relative" ref={resetMenuRef}>
                  <div
                    onClick={() => setShowResetMenu((prev) => !prev)}
                    className={
                      "bottom-2 text-white bold text-[0.8vw] bg-black p-[0.4vw] rounded-md cursor-pointer flex items-center"
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width=".75vw"
                      height="1vw"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M13.65 2.35A7.958 7.958 0 0 0 8 0C3.58 0 .01 3.58.01 8S3.58 16 8 16c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 8 14c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L9 7h7V0l-2.35 2.35z"
                        fill="white"
                      />
                    </svg>
                    <span className="ml-[3px]">Reset</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="0.7vw"
                      height="0.7vw"
                      viewBox="0 0 12 8"
                      fill="none"
                      className={`ml-[5px] transition-transform ${showResetMenu ? "rotate-180" : ""}`}
                    >
                      <path
                        d="M1 1.5L6 6.5L11 1.5"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  {showResetMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-white border border-[#bebebe] rounded-md shadow-md z-10 min-w-[7vw] overflow-hidden">
                      <div
                        onClick={() => {
                          setShowResetMenu(false);
                          handleReset();
                        }}
                        className="px-[0.6vw] py-[0.4vw] text-[0.8vw] text-black cursor-pointer hover:bg-[#F1F1F1]"
                      >
                        Reset
                      </div>
                      {userDetails.role_name !== USER_ROLE.Manager && (
                        <div
                          onClick={() => {
                            setShowResetMenu(false);
                            handleResetAll();
                          }}
                          className="px-[0.6vw] py-[0.4vw] text-[0.8vw] text-black cursor-pointer hover:bg-[#F1F1F1]"
                        >
                          Reset All
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {navItem === "benchmarkBoardSetting" && (
                <div
                  onClick={() => handleBCRecommendationsExcel()}
                  className={
                    "bottom-2 text-white bold text-[0.8vw] bg-[#86BC25] p-[0.4vw] rounded-md cursor-pointer flex items-center"
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="1vw"
                    height="1vw"
                    viewBox="0 0 12 16"
                    fill="none"
                  >
                    <path
                      d="M14.6668 5.33464V9.20797C14.2602 8.97464 13.8135 8.81464 13.3335 8.72797V5.33464H2.66683V12.0013H8.72683C8.6935 12.2213 8.66683 12.4413 8.66683 12.668C8.66683 12.8946 8.6935 13.1146 8.72683 13.3346H2.66683C1.9335 13.3346 1.3335 12.7413 1.3335 12.0013V4.0013C1.3335 3.2613 1.92683 2.66797 2.66683 2.66797H6.66683L8.00016 4.0013H13.3335C14.0668 4.0013 14.6668 4.59464 14.6668 5.33464ZM13.3335 10.668H12.0002V13.3346H10.6668L12.6668 15.3346L14.6668 13.3346H13.3335V10.668Z"
                      fill="white"
                    />
                  </svg>
                  <span className="ml-[3px]">Board Cost Recommendations</span>
                </div>
              )}
            </div>
            {/* )} */}
          </div>

          <div
            className={`bg-[#F1F1F1] grow shrink p-[1.5%] ${showSKUActions ? "" : "pr-[5%]"} flex flex-col flex-nowrap justify-start items-start gap-4 h-full w-full min-w-0 max-w-full overflow-x-hidden`}
          >
            {navItem === "skuBoardAnalysis" && (
              <div className="flex w-full justify-end gap-6 pr-[1vw] items-center flex-wrap">
                <div className="flex gap-1 items-center">
                  <span
                    className={`${!showSKUActions ? "font-bold" : ""} text-[1.4vh] text-gray-700`}
                  >
                    Hide Edits
                  </span>
                  <Toggle
                    checked={showSKUActions}
                    onChange={(checked) => setShowSKUActions(checked)}
                    className="px-0"
                    color="green"
                  />
                  <span
                    className={`${!showSKUActions ? "" : "font-bold"} text-[1.4vh] text-gray-700`}
                  >
                    Show Edits
                  </span>
                </div>
                <div className="flex gap-1 items-center">
                  <span className="bg-deloitteGreen w-[2.5vh] h-[2.5vh]"></span>
                  <span className="font-semibold text-[1.25vh]">
                    {">1.5% Expected PPV"}
                  </span>
                </div>
                <div className="flex gap-1 items-center">
                  <span className="bg-amber w-[2.5vh] h-[2.5vh]"></span>
                  <span className="font-semibold text-[1.25vh]">
                    {"0%-1.5% Expected PPV"}
                  </span>
                </div>
                <div className="flex gap-1 items-center">
                  <span className="bg-alert w-[2.5vh] h-[2.5vh]"></span>
                  <span className="font-semibold text-[1.25vh]">
                    {"<0% PPV"}
                  </span>
                </div>
              </div>
            )}

            <div
              className={`w-full min-w-0 max-w-full overflow-x-auto ${
                navItem === "benchmarkBoardSetting" ? "h-[73vh]" : "h-[65vh]"
              }  business-cost-setting-table`}
            >
              {hasFilterDate ? (
                <>
                  {navItem === "benchmarkBoardSetting" && (
                    <DataTable
                      sortField="district"
                      sortOrder={1}
                      removableSort
                      // className="h-full"
                      value={businessDataCopy}
                      stripedRows
                      scrollable
                      scrollHeight="73vh"
                      showGridlines
                      emptyMessage="No data found for selected commodity"
                    >
                      <Column
                        field="district"
                        header="District"
                        sortable
                        body={(rowdata: BusinessCostData) => {
                          const districtLabel = `${rowdata.district}${
                            treeDistrict.indexOf(rowdata.district) !== -1
                              ? " - Total"
                              : ""
                          }`;
                          return (
                            <div className="flex justify-between items-center gap-[0.5vw]">
                              <span>{districtLabel}</span>
                              {rowdata?.plant &&
                                rowdata?.plant?.length !== 0 && (
                                  <span className="flex-shrink-0">
                                    <Whisper
                                      speaker={
                                        <Tooltip placement="left">
                                          {rowdata?.plant?.join(", ")}
                                        </Tooltip>
                                      }
                                    >
                                      <img
                                        src={ProductInfoIcon}
                                        alt="info"
                                        className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                                      ></img>
                                    </Whisper>
                                  </span>
                                )}
                            </div>
                          );
                        }}
                      ></Column>

                      <Column
                        field="business_cost"
                        header={
                          <div className="float-left">
                            {/* <span className="text-deloitteGreen inline">
                        {moment
                          .utc(currentFilterDate)
                          .subtract(1, "month")
                          .format("MMMM")}
                      </span>{" "} */}
                            <span className="inline">
                              Current Board Cost ($/cft)
                            </span>
                            {refreshDate && (
                              <span className="inline">
                                <Whisper
                                  className="inline"
                                  speaker={
                                    <Tooltip placement="left">
                                      Last refreshed on: {refreshDate}
                                    </Tooltip>
                                  }
                                >
                                  {" "}
                                  <img
                                    src={ProductInfoIcon}
                                    alt="info"
                                    className="w-[1.75vh] cursor-pointer inline"
                                  ></img>
                                </Whisper>
                              </span>
                            )}
                          </div>
                        }
                        body={(rowdata: BusinessCostData) => {
                          return (
                            <div className="flex flex-row justify-between items-center">
                              <span>
                                {shouldShowDashForValue(rowdata.business_cost)
                                  ? "-"
                                  : commodity?.toLowerCase() === "copper wire"
                                    ? `$${formatNumberwithCommas(
                                        rowdata.business_cost,
                                        true,
                                      )}`
                                    : numberFormatter(
                                        rowdata.business_cost,
                                        2,
                                        "$",
                                      )}
                              </span>
                            </div>
                          );
                        }}
                      ></Column>

                      <Column
                        field="expected_po_cost"
                        header="Expected PO Cost ($/cft)"
                        body={(rowdata: BusinessCostData) => {
                          return (
                            <div className="flex flex-row justify-between items-center">
                              <span>
                                {shouldShowDashForValue(
                                  rowdata.expected_po_cost,
                                )
                                  ? "-"
                                  : commodity?.toLowerCase() === "copper wire"
                                    ? `$${formatNumberwithCommas(
                                        rowdata.expected_po_cost ?? 0,
                                        true,
                                      )}`
                                    : numberFormatter(
                                        rowdata.expected_po_cost ?? 0,
                                        2,
                                        "$",
                                      )}
                              </span>
                            </div>
                          );
                        }}
                      ></Column>

                      <Column
                        style={{ maxWidth: "15vw" }}
                        field="business_cost"
                        header={
                          <>
                            <div>
                              <span className="text-deloitteGreen">
                                {commodity !== "Copper Wire" &&
                                  (hasFilterDate
                                    ? moment
                                        .utc(currentFilterDate)
                                        .format("MMMM")
                                    : "No data available")}
                              </span>
                              &nbsp;Calculated Recommendation ($/cft){" "}
                              <Whisper
                                speaker={
                                  <Tooltip placement="left">
                                    Calculated using the following weights:
                                    {commodity?.toLowerCase() ===
                                    "copper wire" ? (
                                      <>
                                        <div> Agent Pricing: 30% </div>
                                        <div> Supplier Quotes: 30% </div>
                                        <div> Index-Implied Cost: 40%</div>
                                        {/* <div> Project Pricing: 15%</div> */}
                                      </>
                                    ) : (
                                      <>
                                        <div> Agent Pricing: 40% </div>
                                        <div> Vendor Quotes: 30% </div>
                                        <div> Index-Implied Cost: 15%</div>
                                        <div> Project Pricing: 15%</div>
                                      </>
                                    )}
                                  </Tooltip>
                                }
                              >
                                <img
                                  className="w-[1.75vh] cursor-pointer inline"
                                  src={ProductInfoIcon}
                                  alt="info"
                                ></img>
                              </Whisper>
                            </div>
                          </>
                        }
                        body={(rowdata: BusinessCostData) => {
                          return withKpiHoverTooltip(
                            rowdata,
                            <span>
                              {shouldShowDashForValue(
                                rowdata.estimated_business_cost,
                              )
                                ? "-"
                                : commodity?.toLowerCase() === "copper wire"
                                  ? `$${formatNumberwithCommas(
                                      rowdata.estimated_business_cost,
                                      true,
                                    )}`
                                  : numberFormatter(
                                      rowdata.estimated_business_cost,
                                      2,
                                      "$",
                                    )}
                            </span>,
                          );
                        }}
                      ></Column>

                      <Column
                        style={{ maxWidth: "15vw" }}
                        field="business_cost"
                        header={
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="text-deloitteGreen">
                                {commodity !== "Copper Wire" &&
                                  (hasFilterDate
                                    ? moment
                                        .utc(currentFilterDate)
                                        .format("MMMM")
                                    : "No data available")}
                              </span>
                              <span>&nbsp;Finalized Board Cost ($/cft)</span>
                            </div>
                          </div>
                        }
                        body={(rowdata: BusinessCostData) => {
                          return rowdata.isEditable ? (
                            <span className="w-full">
                              <InputNumber
                                prefix="$"
                                maxFractionDigits={2}
                                value={rowdata.finalized_business_cost}
                                onChange={(event: InputNumberChangeEvent) => {
                                  setBusinessDataCopy(
                                    businessDataCopy.map((data) => {
                                      if (data.district === rowdata.district) {
                                        data.finalized_business_cost =
                                          event.value || 0;
                                      }
                                      return data;
                                    }),
                                  );
                                }}
                                className="custom-input-number"
                              />
                            </span>
                          ) : (
                            <div className="flex justify-between items-center">
                              <span
                                className={`${
                                  rowdata.validation?.isValid
                                    ? ""
                                    : "text-[#bc2525] font-semibold"
                                }`}
                              >
                                {shouldShowDashForValue(
                                  rowdata.finalized_business_cost,
                                )
                                  ? "-"
                                  : commodity?.toLowerCase() === "copper wire"
                                    ? `$${formatNumberwithCommas(
                                        rowdata.finalized_business_cost,
                                        true,
                                      )}`
                                    : numberFormatter(
                                        rowdata.finalized_business_cost,
                                        2,
                                        "$",
                                      )}
                              </span>
                              {!rowdata.validation?.isValid && (
                                <span
                                  className="cursor-pointer"
                                  onClick={(event) => event.preventDefault()}
                                >
                                  <Whisper
                                    speaker={
                                      <Tooltip placement="left">
                                        {rowdata.validation?.message}
                                      </Tooltip>
                                    }
                                  >
                                    <img src={InfoOutline} alt="info"></img>
                                  </Whisper>
                                </span>
                              )}
                            </div>
                          );
                        }}
                      ></Column>

                      <Column
                        field="adjusted_percent_change"
                        header="Finalized vs Current Variance"
                        sortable
                        sortField="abs_adjusted_percent_change"
                        style={{ minWidth: "10vw" }}
                        body={(rowdata: BusinessCostData) => {
                          return (
                            <div className="flex justify-start items-center gap-2">
                              <span>
                                {(
                                  Number(rowdata.adjusted_percent_change ?? 0) *
                                  100
                                ).toFixed(1)}
                                %
                              </span>
                            </div>
                          );
                        }}
                      ></Column>

                      <Column
                        field="is_reviewed_flag"
                        header="Approval Status"
                        style={{ minWidth: "10vw" }}
                        body={(rowdata: BusinessCostData) => {
                          let msg = `Approved on ${dateFormatterToDDMMMYYYY(
                            rowdata?.approved_on,
                          )} by ${rowdata?.approved_by}.`;
                          let submittedMsg = `Submitted on ${moment
                            .utc(rowdata?.requested_on)
                            .format("DD-MMM-YYYY")} by ${rowdata?.requested_by}.`;
                          return (
                            <>
                              {treeDistrict.indexOf(rowdata.district) === -1 ? (
                                <div className="flex justify-between items-center">
                                  {(() => {
                                    const status = getApprovalStatus(
                                      rowdata.is_reviewed_flag,
                                    );
                                    const isNoChange =
                                      status === APPROVAL_STATUS.NoChange;
                                    return (
                                      <span
                                        className={`border rounded-[50px] font-semibold px-[0.4vw] whitespace-nowrap ${
                                          status === APPROVAL_STATUS.Pending
                                            ? "text-[1.3vh] py-[0.15vh]"
                                            : "py-[0.3vh]"
                                        } ${
                                          isNoChange
                                            ? "border-[#BEBEBE] bg-[#F1F1F1] text-[#5A5A5A]"
                                            : statusClasses[status]
                                        }`}
                                      >
                                        {isNoChange ? "Review Rec" : status}
                                      </span>
                                    );
                                  })()}
                                  {rowdata.is_reviewed_flag > 0 &&
                                    rowdata.is_reviewed_flag !== 3 &&
                                    rowdata.is_reviewed_flag !== 2 &&
                                    rowdata.user_comments && (
                                      <span
                                        className="cursor-pointer"
                                        onClick={(event) =>
                                          event.preventDefault()
                                        }
                                      >
                                        <Whisper
                                          speaker={
                                            <Tooltip placement="left">
                                              {rowdata.user_comments}
                                            </Tooltip>
                                          }
                                        >
                                          <img
                                            src={InfoOutline}
                                            alt="info"
                                          ></img>
                                        </Whisper>
                                      </span>
                                    )}

                                  {getApprovalStatus(
                                    rowdata.is_reviewed_flag,
                                  ) === "Approved" && (
                                    <span
                                      className="cursor-pointer"
                                      onClick={(event) =>
                                        event.preventDefault()
                                      }
                                    >
                                      <Whisper
                                        placement="left"
                                        trigger="hover"
                                        preventOverflow={false}
                                        speaker={<Tooltip>{msg}</Tooltip>}
                                      >
                                        <img
                                          src={ProductInfoIcon}
                                          alt="info"
                                          className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                                        ></img>
                                      </Whisper>
                                    </span>
                                  )}
                                  {userDetails.role_name === USER_ROLE.Lead &&
                                    getApprovalStatus(
                                      rowdata.is_reviewed_flag,
                                    ) === APPROVAL_STATUS.Pending && (
                                      <span
                                        className="cursor-pointer"
                                        onClick={(event) =>
                                          event.preventDefault()
                                        }
                                      >
                                        <Whisper
                                          placement="left"
                                          trigger="hover"
                                          preventOverflow={false}
                                          speaker={
                                            <Tooltip>{submittedMsg}</Tooltip>
                                          }
                                        >
                                          <img
                                            src={ProductInfoIcon}
                                            alt="info"
                                            className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                                          ></img>
                                        </Whisper>
                                      </span>
                                    )}
                                </div>
                              ) : (
                                <></>
                              )}
                            </>
                          );
                        }}
                      ></Column>

                      <Column
                        style={{ minWidth: "15vw", maxWidth: "15vw" }}
                        header="Action"
                        body={(rowdata: BusinessCostData) => {
                          return (
                            <>
                              {treeDistrict.indexOf(rowdata.district) === -1 &&
                              userDetails.userCanEditArray.includes(
                                "Board Cost Setting",
                              ) ? (
                                <div
                                  className={`flex justify-start items-center gap-2`}
                                >
                                  <Button
                                    onClick={() => {
                                      let resetValue = 0;
                                      if (rowdata.isEditable) {
                                        // If it is editable, means the user has asked to cancel it
                                        resetValue = businessData.filter(
                                          (data) =>
                                            data.district === rowdata.district,
                                        )[0].finalized_business_cost;
                                      }
                                      setBusinessDataCopy(
                                        businessDataCopy.map((data) => {
                                          if (
                                            data.district === rowdata.district
                                          ) {
                                            return {
                                              ...data,
                                              finalized_business_cost:
                                                rowdata.isEditable
                                                  ? resetValue
                                                  : data.finalized_business_cost,
                                              isEditable: !data.isEditable,
                                            };
                                          }
                                          return data;
                                        }),
                                      );
                                    }}
                                    img={rowdata.isEditable ? "" : EditIcon}
                                    label={
                                      rowdata.isEditable ? "Cancel" : "Edit"
                                    }
                                    className="grow"
                                    bgColor="#949494"
                                    size="sm"
                                  ></Button>

                                  <Button
                                    className={`${
                                      rowdata.isEditable ? "" : "hidden"
                                    } grow`}
                                    onClick={() => {
                                      setBusinessDataCopy(
                                        businessDataCopy.map((data) => {
                                          if (
                                            data.district === rowdata.district
                                          ) {
                                            let validation = validObj(data);
                                            return {
                                              ...data,
                                              isEditable: !data.isEditable,
                                              adjusted_percent_change:
                                                (data.finalized_business_cost -
                                                  data.business_cost) /
                                                data.business_cost,
                                              validation,
                                            };
                                          }
                                          return data;
                                        }),
                                      );
                                    }}
                                    label="Update"
                                    bgColor="#86BC25"
                                    size="sm"
                                  ></Button>

                                  <Button
                                    className={`${
                                      rowdata.isEditable ? "hidden" : ""
                                    } grow`}
                                    onClick={() => {
                                      handleApproveClick(rowdata);
                                    }}
                                    img={ApproveIcon}
                                    label="Approve"
                                    bgColor="#86BC25"
                                    size="sm"
                                    disabled={handleApproveDisabled(rowdata)}
                                  ></Button>

                                  <Button
                                    className={`${
                                      rowdata.isEditable ? "hidden" : ""
                                    } grow`}
                                    onClick={() => {
                                      handleRetainClick(rowdata);
                                    }}
                                    label="Retain"
                                    bgColor="#0076A8"
                                    size="sm"
                                    disabled={handleApproveDisabled(rowdata)}
                                  ></Button>

                                  <span
                                    onClick={() => {
                                      handleRowReset(rowdata);
                                    }}
                                    title="Reset"
                                    className={`${
                                      rowdata.isEditable ? "hidden" : ""
                                    } flex justify-center items-center bg-black px-[1vh] py-[0.5vh] rounded-md cursor-pointer flex-shrink-0`}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width=".75vw"
                                      height="1vw"
                                      viewBox="0 0 16 16"
                                      fill="none"
                                    >
                                      <path
                                        d="M13.65 2.35A7.958 7.958 0 0 0 8 0C3.58 0 .01 3.58.01 8S3.58 16 8 16c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 8 14c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L9 7h7V0l-2.35 2.35z"
                                        fill="white"
                                      />
                                    </svg>
                                  </span>
                                </div>
                              ) : (
                                <></>
                              )}
                            </>
                          );
                        }}
                      ></Column>
                    </DataTable>
                  )}
                  {navItem === "skuBoardAnalysis" && (
                    <DataTable
                      value={tableData}
                      // sortField={sortField}
                      // sortOrder={sortOrder}
                      //// onSort={handleSort}
                      removableSort
                      // className="h-full"
                      stripedRows
                      scrollable
                      scrollHeight="65vh"
                      showGridlines
                      emptyMessage="No data found for selected commodity"
                      rowClassName={(rowData: any) => {
                        return isHighlightedSkuRow(rowData?.sku)
                          ? "highlight-row-BC"
                          : "";
                      }}
                    >
                      <Column
                        field="sku"
                        // header="SKU"
                        header={() => (
                          <div
                            className="w-full flex justify-between items-center cursor-pointer"
                            onClick={() => handleSort("sku", sortType)}
                          >
                            <div>SKU</div> <div>{renderSortIcon("sku")}</div>
                          </div>
                        )}
                        style={{ minWidth: "10vw" }}
                        body={(rowdata) => {
                          return (
                            <div className="flex justify-between items-center gap-[0.5vw]">
                              <span>{rowdata.sku}</span>
                            </div>
                          );
                        }}
                      ></Column>

                      <Column
                        field="previousMonthPoCost"
                        style={{
                          minWidth: showSKUActions ? "8vw" : "10vw",
                          maxWidth: showSKUActions ? "8vw" : "10vw",
                        }}
                        header={
                          <div>
                            <span
                              className={`${
                                commodity !== "Copper Wire" &&
                                "text-deloitteGreen"
                              }`}
                            >
                              {commodity === "Copper Wire"
                                ? "Current"
                                : hasFilterDate
                                  ? moment
                                      .utc(currentFilterDate)
                                      .subtract(1, "month")
                                      .format("MMMM")
                                  : "No data available"}
                            </span>
                            &nbsp;PO Cost ($/cft)
                          </div>
                        }
                        body={(rowdata) => {
                          return (
                            <div className="flex flex-row justify-between items-center">
                              <span>
                                {rowdata.previousMonthPoCost
                                  ? `$${formatNumberwithCommas(
                                      rowdata.previousMonthPoCost,
                                      true,
                                    )}`
                                  : "-"}
                              </span>
                            </div>
                          );
                        }}
                      ></Column>

                      <Column
                        style={{ minWidth: "10vw" }}
                        field="currentExpectedPoCost"
                        header={
                          <div>
                            <span className="text-deloitteGreen">
                              {commodity !== "Copper Wire" &&
                                (hasFilterDate
                                  ? moment.utc(currentFilterDate).format("MMMM")
                                  : "No data available")}
                            </span>
                            &nbsp;Expected PO Cost ($/cft)
                          </div>
                        }
                        body={(rowdata) => {
                          return (
                            <div className="flex justify-start items-center">
                              <span>
                                {rowdata.currentExpectedPoCost
                                  ? `$${formatNumberwithCommas(
                                      rowdata.currentExpectedPoCost,
                                      true,
                                    )}`
                                  : "-"}
                              </span>
                            </div>
                          );
                        }}
                      ></Column>

                      <Column
                        style={{ minWidth: showSKUActions ? "12vw" : "10vw" }}
                        field="currentBoardCost"
                        header={() => (
                          <div
                            className="w-full flex justify-between items-center cursor-pointer"
                            onClick={() =>
                              handleSort("currentBoardCostPercentage", sortType)
                            }
                          >
                            <div className="float-left">
                              <span className="inline">
                                Current Board Cost ($/cft)
                              </span>
                              <span className="inline">
                                <Whisper
                                  className="inline"
                                  speaker={
                                    <Tooltip placement="left">
                                      {refreshDate
                                        ? `Last refreshed on: ${refreshDate}`
                                        : "Current Board Cost"}
                                    </Tooltip>
                                  }
                                >
                                  {" "}
                                  <img
                                    src={ProductInfoIcon}
                                    alt="info"
                                    className="w-[1.75vh] cursor-pointer inline"
                                  ></img>
                                </Whisper>
                              </span>
                            </div>
                            <div>
                              {renderSortIcon("currentBoardCostPercentage")}
                            </div>
                          </div>
                        )}
                        body={(rowdata) => {
                          return rowdata ? (
                            <div className="flex justify-between items-center gap-2">
                              <span className="whitespace-nowrap">
                                {rowdata.currentBoardCost
                                  ? `$${formatNumberwithCommas(
                                      rowdata.currentBoardCost,
                                      true,
                                    )}`
                                  : "-"}
                              </span>

                              {rowdata.currentBoardCost &&
                              rowdata.currentBoardCostPercentage ? (
                                <span className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                                  {getCommitmentActualPurchasePace(
                                    rowdata.currentBoardCostPercentage,
                                  )}
                                  <span
                                    className={`${
                                      rowdata.currentBoardCostPercentage < 0
                                        ? "text-alert"
                                        : rowdata.currentBoardCostPercentage >=
                                              0 &&
                                            rowdata.currentBoardCostPercentage <=
                                              1.5
                                          ? "text-amber"
                                          : "text-deloitteGreen"
                                    }`}
                                  >
                                    {/* {rowdata.currentBoardCostPercentage}% */}
                                    {`${trunc(
                                      rowdata?.currentBoardCostPercentage,
                                    )}%`}
                                  </span>
                                </span>
                              ) : (
                                <>{"-"}</>
                              )}
                            </div>
                          ) : null;
                        }}
                      ></Column>

                      <Column
                        field="finalizedBoardCost"
                        header={() => (
                          <div
                            className="w-full flex justify-between items-center cursor-pointer"
                            onClick={() =>
                              handleSort(
                                "finalizedBoardCostPercentage",
                                sortType,
                              )
                            }
                          >
                            <div>Finalized Board Cost ($/cft)</div>{" "}
                            <div>
                              {renderSortIcon("finalizedBoardCostPercentage")}
                            </div>
                          </div>
                        )}
                        style={{ minWidth: showSKUActions ? "12vw" : "10vw" }}
                        body={(rowdata) => {
                          return rowdata?.isEditable ? (
                            <span className="w-full">
                              <InputNumber
                                prefix="$"
                                maxFractionDigits={2}
                                value={rowdata.finalizedBoardCost}
                                onChange={(event: InputNumberChangeEvent) => {
                                  setTableData(
                                    tableData.map((data: any) => {
                                      if (
                                        data.base_sku === rowdata.base_sku &&
                                        data.district === rowdata.district &&
                                        (data.sku || data.catalogue) ===
                                          (rowdata.sku || rowdata.catalogue)
                                      ) {
                                        return {
                                          ...data,
                                          finalizedBoardCost: event.value || 0,
                                        };
                                      }

                                      return data;
                                    }),
                                  );
                                }}
                                className="custom-input-number"
                              />
                            </span>
                          ) : (
                            <div className="flex justify-between items-center gap-2">
                              <span className="whitespace-nowrap">
                                {rowdata?.finalizedBoardCost
                                  ? `$${formatNumberwithCommas(
                                      rowdata.finalizedBoardCost,
                                      true,
                                    )}`
                                  : "-"}
                              </span>

                              {rowdata.finalizedBoardCost &&
                              rowdata.finalizedBoardCostPercentage ? (
                                <span className="flex items-center gap-1 flex-shrink-0 whitespace-nowrap">
                                  {getCommitmentActualPurchasePace(
                                    rowdata.finalizedBoardCostPercentage,
                                  )}
                                  <span
                                    className={`${
                                      rowdata.finalizedBoardCostPercentage < 0
                                        ? "text-alert"
                                        : rowdata.finalizedBoardCostPercentage >=
                                              0 &&
                                            rowdata.finalizedBoardCostPercentage <=
                                              1.5
                                          ? "text-amber"
                                          : "text-deloitteGreen"
                                    }`}
                                  >
                                    {`${trunc(
                                      rowdata.finalizedBoardCostPercentage,
                                    )}%`}
                                  </span>
                                </span>
                              ) : (
                                <>-</>
                              )}
                            </div>
                          );
                        }}
                      ></Column>

                      <Column
                        field="finalizedBoardCostPercentage"
                        header={<span>Finalized vs Current Variance</span>}
                        style={{
                          minWidth: showSKUActions ? "9vw" : "11vw",
                          maxWidth: showSKUActions ? "9vw" : "11vw",
                        }}
                        body={(rowdata) => {
                          const finalizedVsCurrentVariance =
                            getFinalizedVsCurrentVariance(rowdata);

                          return finalizedVsCurrentVariance !== null ? (
                            <span className="text-black whitespace-nowrap">
                              {`${trunc(finalizedVsCurrentVariance)}%`}
                            </span>
                          ) : (
                            <>-</>
                          );
                        }}
                      ></Column>

                      {showSKUActions && (
                        <Column
                          field="is_reviewed_flag"
                          header={<span>Approval Status</span>}
                          style={{
                            minWidth: showSKUActions ? "8.5vw" : "10vw",
                            maxWidth: showSKUActions ? "8.5vw" : "10vw",
                          }}
                          body={(rowdata: BusinessCostData) => {
                            if (isHighlightedSkuRow((rowdata as any)?.sku)) {
                              return <></>;
                            }

                            const reviewedFlag = rowdata?.is_reviewed_flag;

                            let msg = `Approved on ${dateFormatterToDDMMMYYYY(
                              rowdata?.approved_on,
                            )} by ${rowdata?.approved_by}.`;
                            let submittedMsg = `Submitted on ${dateFormatterToDDMMMYYYY(
                              rowdata?.refresh_date,
                            )} by ${rowdata?.requested_by}.`;
                            return (
                              <div className="flex justify-between items-center">
                                <span
                                  className={`border rounded-[50px] font-semibold px-[0.4vw] whitespace-nowrap ${
                                    getApprovalStatus(reviewedFlag) ===
                                    APPROVAL_STATUS.Pending
                                      ? "text-[1.3vh] py-[0.15vh]"
                                      : "py-[0.3vh]"
                                  } ${
                                    statusClasses[
                                      getApprovalStatus(reviewedFlag)
                                    ]
                                  }`}
                                >
                                  {getApprovalStatus(reviewedFlag)}
                                </span>
                                {reviewedFlag > 0 &&
                                  reviewedFlag !== 3 &&
                                  reviewedFlag !== 2 &&
                                  rowdata.user_comments && (
                                    <span
                                      className="cursor-pointer"
                                      onClick={(event) =>
                                        event.preventDefault()
                                      }
                                    >
                                      <Whisper
                                        speaker={
                                          <Tooltip placement="left">
                                            {rowdata.user_comments}
                                          </Tooltip>
                                        }
                                      >
                                        <img src={InfoOutline} alt="info"></img>
                                      </Whisper>
                                    </span>
                                  )}
                                {getApprovalStatus(reviewedFlag) ===
                                  "Approved" && (
                                  <span
                                    className="cursor-pointer"
                                    onClick={(event) => event.preventDefault()}
                                  >
                                    <Whisper
                                      placement="left"
                                      trigger="hover"
                                      preventOverflow={false}
                                      speaker={<Tooltip>{msg}</Tooltip>}
                                    >
                                      <img
                                        src={ProductInfoIcon}
                                        alt="info"
                                        className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                                      ></img>
                                    </Whisper>
                                  </span>
                                )}
                                {userDetails.role_name === USER_ROLE.Lead &&
                                  getApprovalStatus(reviewedFlag) ===
                                    APPROVAL_STATUS.Pending && (
                                    <span
                                      className="cursor-pointer"
                                      onClick={(event) =>
                                        event.preventDefault()
                                      }
                                    >
                                      <Whisper
                                        placement="left"
                                        trigger="hover"
                                        preventOverflow={false}
                                        speaker={
                                          <Tooltip>{submittedMsg}</Tooltip>
                                        }
                                      >
                                        <img
                                          src={ProductInfoIcon}
                                          alt="info"
                                          className="w-[1.5vh] h-[1.5vh] cursor-pointer"
                                        ></img>
                                      </Whisper>
                                    </span>
                                  )}
                              </div>
                            );
                          }}
                        ></Column>
                      )}

                      {showSKUActions && (
                        <Column
                          style={{ minWidth: "12vw", maxWidth: "12vw" }}
                          header="Action"
                          body={(rowdata: BusinessCostData) => {
                            return (
                              <>
                                {userDetails.userCanEditArray.includes(
                                  "Board Cost Setting",
                                ) &&
                                !isHighlightedSkuRow((rowdata as any)?.sku) ? (
                                  <div
                                    className={`flex justify-start items-center gap-2`}
                                  >
                                    <Button
                                      onClick={() => {
                                        let resetValue = 0;
                                        if (rowdata.isEditable) {
                                          const originalSkuRow = skuData.filter(
                                            (data: any) =>
                                              data.base_sku ===
                                                rowdata.base_sku &&
                                              data.district ===
                                                rowdata.district &&
                                              (data.sku || data.catalogue) ===
                                                ((rowdata as any).sku ||
                                                  (rowdata as any).catalogue),
                                          )[0] as any;
                                          resetValue =
                                            originalSkuRow?.finalizedBoardCost ??
                                            originalSkuRow?.finalized_business_cost ??
                                            0;
                                        }
                                        setTableData(
                                          tableData.map((data: any) => {
                                            const isCurrentRow =
                                              data.base_sku ===
                                                rowdata.base_sku &&
                                              data.district ===
                                                rowdata.district &&
                                              (data.sku || data.catalogue) ===
                                                ((rowdata as any).sku ||
                                                  (rowdata as any).catalogue);

                                            if (isCurrentRow) {
                                              return {
                                                ...data,
                                                finalizedBoardCost:
                                                  rowdata.isEditable
                                                    ? resetValue
                                                    : data.finalizedBoardCost,
                                                isEditable: !data.isEditable,
                                              };
                                            }

                                            // Ensure only one row can stay in edit mode at a time.
                                            return {
                                              ...data,
                                              isEditable: false,
                                            };
                                          }),
                                        );
                                      }}
                                      img={rowdata.isEditable ? "" : EditIcon}
                                      label={
                                        rowdata.isEditable ? "Cancel" : "Edit"
                                      }
                                      className="grow"
                                      bgColor="#949494"
                                      size="sm"
                                    ></Button>

                                    <Button
                                      className={`${
                                        rowdata.isEditable ? "" : "hidden"
                                      } grow`}
                                      onClick={() => {
                                        setTableData(
                                          tableData.map((data: any) => {
                                            const isCurrentRow =
                                              data.base_sku ===
                                                rowdata.base_sku &&
                                              data.district ===
                                                rowdata.district &&
                                              (data.sku || data.catalogue) ===
                                                ((rowdata as any).sku ||
                                                  (rowdata as any).catalogue);

                                            if (isCurrentRow) {
                                              return {
                                                ...data,
                                                isEditable: false,
                                              };
                                            }

                                            return data;
                                          }),
                                        );
                                      }}
                                      label="Update"
                                      bgColor="#86BC25"
                                      size="sm"
                                    ></Button>

                                    <Button
                                      className={`${
                                        rowdata.isEditable ? "hidden" : ""
                                      } grow`}
                                      onClick={() => {
                                        handleSKUBoardCostApprove(rowdata);
                                      }}
                                      img={ApproveIcon}
                                      label="Approve"
                                      bgColor="#86BC25"
                                      size="sm"
                                    ></Button>
                                  </div>
                                ) : (
                                  <></>
                                )}
                              </>
                            );
                          }}
                        ></Column>
                      )}
                    </DataTable>
                  )}
                </>
              ) : (
                <div className="text-center p-10 w-full bg-white">
                  No Valid contacts Found
                </div>
              )}
            </div>
            {navItem === "skuBoardAnalysis" && hasFilterDate && (
              <div className="w-full px-[1vw] text-[1.6vh] text-[#858585]">
                *A Current Board Cost with negative expected PPV (red arrow)
                signals that this SKU’s Board Cost must be changed to avoid
                negative PPV
              </div>
            )}
          </div>
        </div>
      )}

      <Dialog
        draggable={false}
        visible={showApprovalDialog}
        modal
        header={HeaderElement}
        footer={FooterElement}
        style={{ width: "35vw" }}
        closable={false}
        onHide={() => {
          setShowApprovalDialog(false);
        }}
      >
        <div className="flex flex-col justify-start items-start py-[2vh] px-[1vw] gap-4 radio-criteria approval-dialog">
          <span className="text-fs16 font-normal">
            Approval required to finalize Board Cost. If needed, enter rationale
            below:
          </span>
          <div className="flex justify-start items-center gap-2">
            <RadioButton
              variant="filled"
              inputId="leadApproval"
              className={approvalCriteria === "leadApproval" ? "checked" : ""}
              name="approvalCriteria"
              value={approvalCriteria}
              disabled={approvalCriteria === "self"}
              checked={approvalCriteria === "leadApproval"}
            />
            <label
              htmlFor="leadApproval"
              className={`text-fs16 ${
                approvalCriteria === "self" ? "opacity-50" : "opacity-100"
              }`}
            >
              Send Request for Approval
            </label>
          </div>

          <div className="flex flex-col justify-start items-start gap-1 w-full">
            <span className="text-fs16">Comment</span>
            <InputTextarea
              maxLength={300}
              rows={4}
              autoResize
              className="w-full p-[1vh] text-fs16"
              value={approvalComment}
              onChange={(e) => setApprovalComment(e.target.value)}
            />
            <span className="text-gray-500 self-end text-fs12">
              Max characters allowed 300
            </span>
          </div>
          <div
            className={`flex justify-start items-center gap-2 bg-[#FFE4E8] rounded px-[1vw] py-[0.5vh] self-stretch ${
              updateError.isError ? "visible" : "invisible"
            }`}
          >
            <img src={InfoOutline}></img>
            <span className="text-fs14 font-semibold">
              {updateError.message}
            </span>
          </div>
        </div>
      </Dialog>

      {wireApprovalRow && (
        <WirePopupModal
          district={wireApprovalRow.district}
          onClose={closeWirePopup}
          items={wirePopupItems}
          isOpen={isWirePopupOpen}
          onApproveAll={
            isWireRetainFlow ? submitWireRetain : submitWireApproval
          }
          onSubmit12STR={
            isWireRetainFlow ? handleWireRetain12STR : handleWireSubmit12STR
          }
          isRetainFlow={isWireRetainFlow}
        />
      )}

      {openModal && (
        <CustomModal
          openModal={openModal}
          closeModal={() => {
            setOPenModal(false);
            setDistrictArray([]);
          }}
          isloading={isSKUBoardDownload}
          header="Download SKU Level Board Cost"
          footer=""
        >
          <div className="flex flex-col justify-between items-center px-[1vh]">
            <div className="flex w-full justify-start items-center px-[1vh] pb-[2.5vh]">
              <span className="text-[0.9vw] font-semibold">
                Download Board Cost For:
              </span>
              <span className="text-[0.9vw] font-semibold pl-[0.75vh] underline text-deloitteGreen">
                {/* <span className="font-bold underline text-deloitteGreen"> */}
                {hasFilterDate
                  ? convertDateToMonthYear(currentFilterDate)
                  : "No data available"}
              </span>
            </div>
            <div className="flex w-full gap-[2vw] px-[1vh] pb-[2vh]">
              <div className="flex flex-col justify-start items-start w-full">
                <label className="font-bold text-fs16">Select Districts</label>
                <CheckPicker
                  // sticky
                  countable={true}
                  data={filters?.districtforskuDownload}
                  preventOverflow={true}
                  cleanable={true}
                  style={{ width: "60%" }}
                  value={districtArray}
                  onClean={() => {
                    setDistrictArray([]);
                  }}
                  onChange={(value: string[]) => {
                    if (value) {
                      setDistrictArray(value);
                    }
                  }}
                  placeholder="All"
                  labelKey="district"
                  valueKey="district"
                  menuMaxHeight={150}
                />
              </div>
            </div>
            <div className="flex w-full gap-[2vw] px-[1vh] pb-[2vh]">
              {districtArray.length !== 0 && (
                <div>
                  <span className="text-[0.6vw] font-bold">
                    Selected Districts:{" "}
                  </span>
                  <span className="text-[0.6vw] font-semibold">
                    {districtArray.map((ele, i) => (
                      <span>
                        {`${ele}`}
                        {districtArray.length === i + 1 ? "" : ", "}
                      </span>
                    ))}
                  </span>
                </div>
              )}
            </div>
            <div className="flex justify-center px-[1vh] pt-[2vh]">
              <Button
                className="w-[5vw]"
                label={"Download"}
                bgColor={"#86BC25"}
                onClick={
                  commodity === "Copper Wire"
                    ? () => handleSKULevelBCExcelForWire()
                    : () => handleSKULevelBCExcel()
                }
                // disabled={!districtArray.length}
              />
            </div>
          </div>
        </CustomModal>
      )}
    </>
  );
};

export default BusinessCostSetting;
